<template>
  <div
    class="factors-viz-container"
    :class="{
      'factors-viz-container--network': useFactorBaseRevealData && dataVizTab === 'network',
      'factors-viz-container--factor-data': useFactorBaseRevealData && heatmapPairCount,
    }"
    :style="containerStyle"
  >
    <div v-if="error" class="error-alert">
      {{ error }}
    </div>
    <div v-if="useFactorBaseRevealData && heatmapPairCount && cellColorMode !== 'factorValue' && showGroupByControls" class="mb-2 d-flex flex-nowrap align-items-center overflow-auto fbr-heatmap-toolbar">
      <div class="fbr-heatmap-control-group">
        <label for="fbr-heatmap-group-mode-select" class="mb-0 font-weight-bold small text-secondary fbr-heatmap-toolbar-label">Group by:</label>
        <b-form-select
          id="fbr-heatmap-group-mode-select"
          v-model="heatmapGroupMode"
          :options="heatmapGroupModeOptions"
          size="sm"
          class="form-control form-control-sm"
          style="max-width: 200px;"
        />
      </div>
      <div
        v-if="heatmapGroupMode !== 'all' && heatmapGroupOptions.length"
        class="fbr-heatmap-control-group fbr-heatmap-group-by-subcontrols"
      >
        <label for="fbr-heatmap-group-select" class="mb-0 font-weight-bold small text-secondary fbr-heatmap-toolbar-label">{{ heatmapGroupSelectLabel }}:</label>
        <b-form-select
          id="fbr-heatmap-group-select"
          v-model="selectedHeatmapGroup"
          :options="heatmapGroupOptions"
          size="sm"
          class="form-control form-control-sm"
          style="max-width: 420px;"
        />
      </div>
    </div>
    <div
      :class="useFactorBaseRevealData && heatmapPairCount ? 'fbr-data-viz-ledger mb-2' : ''"
    >
      <div
        v-if="useFactorBaseRevealData && heatmapPairCount"
        class="fbr-data-viz-toolbar"
      >
        <div
          class="fbr-data-viz-tabs"
          role="tablist"
          aria-label="Data visualization"
        >
          <button
            type="button"
            role="tab"
            class="fbr-data-viz-tab"
            :class="{ 'is-active': dataVizTab === 'heatmap' }"
            :aria-selected="dataVizTab === 'heatmap' ? 'true' : 'false'"
            @click="dataVizTab = 'heatmap'"
          >
            Heatmap
          </button>
          <button
            type="button"
            role="tab"
            class="fbr-data-viz-tab"
            :class="{ 'is-active': dataVizTab === 'network' }"
            :aria-selected="dataVizTab === 'network' ? 'true' : 'false'"
            @click="dataVizTab = 'network'"
          >
            Graph network
          </button>
        </div>
        <div class="fbr-data-viz-toolbar-end">
          <workflow-viz-toolbar-menus
            v-if="showWorkflowSelectionChrome"
            :selected-nodes="selectedNodes"
            :saved-explanations="savedExplanations"
            :saved-dataset-runs="savedDatasetRuns"
            @analyze-explain="$emit('analyze-explain')"
            @analyze-provenance="$emit('analyze-provenance')"
            @open-saved-explanation="$emit('open-saved-explanation', $event)"
            @open-saved-dataset="$emit('open-saved-dataset', $event)"
          />
        </div>
      </div>
      <div
        :class="useFactorBaseRevealData && heatmapPairCount ? 'fbr-data-viz-panel' : ''"
      >
        <div
          v-if="showFactorValueColumnFilters"
          class="fbr-data-viz-view-filters"
          role="group"
          aria-label="Visualization filters"
        >
          <label
            class="fbr-heatmap-filter-toggle mb-0"
            :class="{ 'is-locked': geneSetClustersFilterLocked }"
            :title="geneSetClustersFilterLocked ? 'Required on Y-axis for current Render by' : null"
          >
            <input
              type="checkbox"
              :checked="effectiveShowGeneSetClusters"
              :disabled="geneSetClustersFilterLocked"
              @change="onHeatmapViewFilterChange('showGeneSetClusters', $event.target.checked)"
            />
            <span>Gene set clusters</span>
          </label>
          <label
            class="fbr-heatmap-filter-toggle mb-0"
            :class="{ 'is-locked': geneSetsFilterLocked }"
            :title="geneSetsFilterLocked ? 'Required on Y-axis for current Render by' : null"
          >
            <input
              type="checkbox"
              :checked="effectiveShowGeneSets"
              :disabled="geneSetsFilterLocked"
              @change="onHeatmapViewFilterChange('showGeneSets', $event.target.checked)"
            />
            <span>Gene sets</span>
          </label>
          <label class="fbr-heatmap-filter-toggle mb-0">
            <input
              type="checkbox"
              :checked="viewFilters.showGenes"
              @change="onHeatmapViewFilterChange('showGenes', $event.target.checked)"
            />
            <span>Genes</span>
          </label>
          <label class="fbr-heatmap-filter-toggle mb-0">
            <input
              type="checkbox"
              :checked="viewFilters.genesInSearchOnly"
              @change="onHeatmapViewFilterChange('genesInSearchOnly', $event.target.checked)"
            />
            <span>Genes in search</span>
          </label>
          <label class="fbr-heatmap-filter-toggle mb-0">
            <input
              type="checkbox"
              :checked="viewFilters.onlySelected"
              @change="onHeatmapViewFilterChange('onlySelected', $event.target.checked)"
            />
            <span>Only selected</span>
          </label>
          <div
            v-if="dataVizTab === 'heatmap'"
            class="fbr-heatmap-axis-layout"
          >
            <label for="fbr-heatmap-axis-layout-select" class="fbr-heatmap-axis-layout-label mb-0">Render by:</label>
            <select
              id="fbr-heatmap-axis-layout-select"
              v-model="heatmapAxisLayout"
              class="form-control form-control-sm fbr-heatmap-axis-layout-select"
              aria-label="Render heatmap by"
            >
              <option value="factorsRows">Gene set clusters × gene sets &amp; genes</option>
              <option value="geneSetsRows">Gene sets × gene set clusters &amp; genes</option>
            </select>
          </div>
        </div>
    <!-- Gene-set entry heatmap legend (network has its own legend) -->
    <div
      v-if="showFactorValueColumnFilters && dataVizTab === 'heatmap'"
      class="viz-legend viz-legend--plain"
    >
      <div class="legend-content">
        <div class="legend-item">
          <span class="legend-color-item">
            <span class="legend-color factor-relevance" aria-hidden="true"></span>
            <span class="legend-color-text">Overall gene set cluster value</span>
          </span>
        </div>
      </div>
      <div class="legend-actions">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
          @click="downloadHeatmap"
        >
          <b-icon icon="download" class="mr-1" aria-hidden="true" />
          Heatmap
        </button>
      </div>
    </div>
    <div
      v-show="!useFactorBaseRevealData || dataVizTab === 'heatmap'"
      class="fbr-data-viz-heatmap-pane"
    >
    <div
      class="viz-legend"
      v-if="
        !loading &&
        cellColorMode !== 'factorValue' &&
        (useFactorBaseRevealData
          ? heatmapDataFromFactorData && heatmapDataFromFactorData.ready
          : heatmapData && heatmapData.genes && heatmapData.genes.length > 0)
      "
    >
      <div class="legend-content">
        <div v-if="showAssociationLegend" class="legend-item">
          <span class="legend-label">Phenotype associations:</span>
          <label class="legend-filter-toggle">
            <input
              type="checkbox"
              class="legend-filter-checkbox very-strong"
              :checked="associationFilters.veryStrong"
              @change="onAssociationFilterChange('veryStrong', $event.target.checked)"
            />
            <span class="legend-color-text">Very Strong (&gt; 3)</span>
          </label>
          <label class="legend-filter-toggle">
            <input
              type="checkbox"
              class="legend-filter-checkbox strongly-suggestive"
              :checked="associationFilters.stronglySuggestive"
              @change="onAssociationFilterChange('stronglySuggestive', $event.target.checked)"
            />
            <span class="legend-color-text">Strongly Suggestive (2-3)</span>
          </label>
          <label class="legend-filter-toggle">
            <input
              type="checkbox"
              class="legend-filter-checkbox nominally-significant"
              :checked="associationFilters.nominallySignificant"
              @change="onAssociationFilterChange('nominallySignificant', $event.target.checked)"
            />
            <span class="legend-color-text">Nominally Significant (1-2)</span>
          </label>
          <label class="legend-filter-toggle">
            <input
              type="checkbox"
              class="legend-filter-checkbox not-significant"
              :checked="associationFilters.notSignificant"
              @change="onAssociationFilterChange('notSignificant', $event.target.checked)"
            />
            <span class="legend-color-text">Not Significant (&lt; 1)</span>
          </label>
        </div>
        <div
          v-if="emphasizeSearchContextGenes && heatmapHasSearchContextGeneColumns"
          class="legend-item mt-1"
        >
          <span class="legend-label">Gene columns:</span>
          <span class="legend-color-item">
            <span class="legend-gene-sample legend-gene-sample-search">GENE</span>
            <span class="legend-color-text">Search gene (bold)</span>
          </span>
          <span class="legend-color-item">
            <span class="legend-gene-sample legend-gene-sample-context">GENE</span>
            <span class="legend-color-text">Other genes on gene set cluster</span>
          </span>
        </div>
      </div>
      <div class="legend-actions">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
          @click="downloadHeatmap"
        >
          <b-icon icon="download" class="mr-1" aria-hidden="true" />
          Heatmap
        </button>
      </div>
    </div>
    <div id="factor-base-reveal-heatmap-wrapper" class="heatmap-container">
      <div
        v-if="useFactorBaseRevealData && (!heatmapDataFromFactorData || !heatmapDataFromFactorData.ready)"
        class="text-center p-3 text-muted"
      >
        <template v-if="!heatmapPairCount">No phenotypes or data categories available to plot.</template>
        <template v-else-if="effectiveHeatmapGroupMode !== 'all' && !selectedHeatmapGroup">Select a {{ effectiveHeatmapGroupMode === 'direction' ? 'data category' : 'phenotype' }}.</template>
        <template v-else-if="cellColorMode === 'factorValue' && viewFilters.onlySelected && !(selectedNodes && selectedNodes.length)">Select rows or columns to show with Only selected.</template>
        <template v-else>No matrix data for this selection.</template>
      </div>
      <div v-else-if="!useFactorBaseRevealData && (!heatmapData || !heatmapData.genes || heatmapData.genes.length === 0)" class="text-center p-3 text-muted">
        Loading heatmap data...
      </div>
      <div v-else class="heatmap-scrollable-wrapper">
        <div class="heatmap-labels-fixed" ref="heatmapLabelsContainer"></div>
        <div class="heatmap-content-scrollable" ref="heatmapContainer"></div>
      </div>
    </div>
    </div>
    <div
      v-if="useFactorBaseRevealData && dataVizTab === 'network'"
      class="fbr-data-network-panel"
    >
      <div
        v-if="!heatmapPairCount"
        class="text-center p-3 text-muted"
      >
        No phenotypes or data categories available to plot.
      </div>
      <div
        v-else-if="effectiveHeatmapGroupMode !== 'all' && !selectedHeatmapGroup"
        class="text-center p-3 text-muted"
      >
        Select a {{ effectiveHeatmapGroupMode === 'direction' ? 'data category' : 'phenotype' }}.
      </div>
      <div
        v-else-if="!dataViewNetwork.nodes.length"
        class="text-center p-3 text-muted"
      >
        <template v-if="cellColorMode === 'factorValue' && viewFilters.onlySelected && !(selectedNodes && selectedNodes.length)">
          Select rows or columns to show with Only selected.
        </template>
        <template v-else>No network data for this selection.</template>
      </div>
      <div
        v-else
        class="fbr-network-viz-wrap"
        :style="{ minHeight: dataViewNetworkHeight + 'px' }"
      >
        <div
          v-if="dataVizNetworkRendering"
          class="fbr-network-loading-overlay"
          role="status"
          aria-live="polite"
        >
          <b-spinner small type="grow" variant="secondary" />
          <span class="ml-2 small text-muted">Rendering network…</span>
        </div>
        <factor-base-reveal-network
          :key="dataViewNetworkKey"
          :network="dataViewNetwork"
          :genes="dataViewNetworkGenes"
          :height="dataViewNetworkHeight"
          :selected-nodes="selectedNodes"
          :suppress-selection-highlight="cellColorMode === 'factorValue' && viewFilters.onlySelected"
          @update:selectedNodes="$emit('update:selectedNodes', $event)"
          :gene-node-metric-key="cellColorMode === 'factorValue' ? 'node_score' : 'combined_score'"
          :gene-set-node-metric-key="cellColorMode === 'factorValue' ? 'node_score' : ''"
          :edge-distance-metric-key="cellColorMode === 'factorValue' ? 'factor_value' : ''"
          :gene-color-by-gwas-support="cellColorMode !== 'factorValue'"
          :node-selection-enabled="true"
          @network-ready="onDataViewNetworkReady"
        />
      </div>
    </div>
    </div>
    </div>
    <div v-if="loading" class="loading">Loading visualization...</div>
    <workflow-heatmap-node-action-menu
      v-if="useFactorBaseRevealData"
      :open="heatmapNodeMenuOpen"
      :target="heatmapNodeMenuTarget"
      :is-selected="heatmapNodeMenuIsSelected"
      :left="heatmapNodeMenuLeft"
      :top="heatmapNodeMenuTop"
      @close="closeHeatmapNodeMenu"
      @toggle-select="onHeatmapNodeMenuToggle"
    />
  </div>
</template>
<script>
import Vue from "vue";
import JSZip from "jszip";
import { resolveCfdePhenotypeLabel, resolveCfdeFactorClusterDisplayLabel } from "@/utils/cfdeUtils";
import FactorBaseRevealNetwork from "./FactorBaseRevealNetwork2.vue";
import WorkflowHeatmapNodeActionMenu from "./revealMultiQueryWorkflow/WorkflowHeatmapNodeActionMenu.vue";
import WorkflowVizToolbarMenus from "./revealMultiQueryWorkflow/WorkflowVizToolbarMenus.vue";
import {
  buildMergedFactorDataNetwork,
  genesFromFactorDataNetwork,
} from "./factorRevealDataNetwork.js";
import {
  HEATMAP_SELECTION_KIND,
  DEFAULT_HEATMAP_VIEW_FILTERS,
  normalizeHeatmapViewFilters,
  buildCrossingSelectionNode,
  buildGeneSelectionNode,
  buildGeneSetSelectionNode,
  buildRowSelectionNode,
  isHeatmapCellHighlighted,
  isHeatmapColHighlighted,
  isHeatmapRowHighlighted,
  isSelectionNodeSelected,
  removeSelectionNode,
  toggleSelectionNode,
} from "./revealMultiQueryWorkflow/revealMqHeatmapSelection.js";
import {
  DEFAULT_ASSOCIATION_FILTERS,
  associationTierPasses,
} from "./revealMultiQueryWorkflow/revealMqAssociationScore.js";

export default Vue.component("pigean-factors-viz", {
  components: {
    FactorBaseRevealNetwork,
    WorkflowHeatmapNodeActionMenu,
    WorkflowVizToolbarMenus,
  },
  props: {
    pigeanFactorData: {
      type: Array,
      default: () => []
    },
    phenotypeName: {
      type: String,
      default: ""
    },
    height: {
      type: String,
      default: "600px"
    },
    heatmapData: {
      type: Object,
      default: () => ({ genes: [], factors: [], data: [] })
    },
    geneSetsHeatmapData: {
      type: Object,
      default: () => ({ geneSets: [], factors: [], data: [] })
    },
    /** Factor data from FactorBaseReveal: { [phenotype]: { genes: {}, factors: [] } }. Used when embedded in factors section. */
    factorData: {
      type: Object,
      default: () => ({})
    },
    /** Table rows from FactorBaseReveal (phenotype, factor, factorLabel, top_gene_sets). Same data as the View data table. */
    factorDataTableRows: {
      type: Array,
      default: () => []
    },
    /** Map phenotype id -> description for display (from FactorBaseReveal). */
    phenotypeDescriptionById: {
      type: Object,
      default: () => ({})
    },
    /** Canvas-style selected heatmap nodes (traits, gene sets, genes, crossings). */
    selectedNodes: {
      type: Array,
      default: () => [],
    },
    /** Multi Query workflow: Analyze / Explanations / Datasets menus in viz tab bar. */
    showWorkflowSelectionChrome: {
      type: Boolean,
      default: false,
    },
    savedExplanations: {
      type: Array,
      default: () => [],
    },
    savedDatasetRuns: {
      type: Array,
      default: () => [],
    },
    /**
     * Y-axis body row label: "phenotype" (default, text-query) or "factor" (gene-set entry —
     * many factors share one phenotype id, so the factor/cluster label is the useful row text).
     */
    rowLabelMode: {
      type: String,
      default: "phenotype",
      validator: (v) => v === "phenotype" || v === "factor",
    },
    /**
     * Gene-set entry: bold search-gene column headers vs normal context genes,
     * and show the matching legend entries.
     */
    emphasizeSearchContextGenes: {
      type: Boolean,
      default: false,
    },
    /**
     * association (default): Combined/GWAS header + binary gene-set cells + [0,1] purple genes.
     * factorValue (gene-set entry / factorization): no score header; gene-set + gene cells colored
     * by Overall factor value (purple shades); columns sorted by |factor_value|.
     */
    cellColorMode: {
      type: String,
      default: "association",
      validator: (v) => v === "association" || v === "factorValue",
    },
    /** When false, hide Combined / GWAS / Gene-set support header rows. */
    showAssociationScoreHeader: {
      type: Boolean,
      default: true,
    },
    /** When false, hide Phenotype associations (Combined-tier) legend checkboxes. */
    showAssociationLegend: {
      type: Boolean,
      default: true,
    },
    /**
     * When false, hide Group by controls and always show every phenotype×factor row
     * (used by Multi Query free-text after single-direction retrieval).
     */
    showGroupByControls: {
      type: Boolean,
      default: true,
    },
    /** When false, omit `[Data category]` / direction prefix on heatmap row labels. */
    showDirectionInRowLabels: {
      type: Boolean,
      default: true,
    },
    /** Phenotype-association Combined-score tier checkboxes (legend filters). */
    phenotypeAssociationFilters: {
      type: Object,
      default: () => ({ ...DEFAULT_ASSOCIATION_FILTERS }),
    },
    /**
     * Gene-set entry view filters shared with the Data table (parent-owned; emit updates).
     * Filtering is view-only — underlying factorData is never mutated.
     */
    heatmapViewFilters: {
      type: Object,
      default: () => ({ ...DEFAULT_HEATMAP_VIEW_FILTERS }),
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      heatmapNodeMenuOpen: false,
      heatmapNodeMenuLeft: 0,
      heatmapNodeMenuTop: 0,
      heatmapNodeMenuTarget: null,
      /** phenotype | direction | all — controls heatmap row grouping. */
      heatmapGroupMode: "phenotype",
      /** Selected phenotype id or data category label for the active group mode (unused when Group by is Show all). */
      selectedHeatmapGroup: "",
      /** heatmap | network */
      dataVizTab: "heatmap",
      dataVizNetworkRendering: false,
      dataVizNetworkRenderTimerId: null,
      /**
       * Gene-set entry (factorValue) axis layout:
       * factorsRows = factors (Y) × gene sets & genes (X) — default
       * geneSetsRows = gene sets (Y) × factors & genes (X)
       */
      heatmapAxisLayout: "factorsRows",
    };
  },
  computed: {
    associationFilters() {
      return {
        ...DEFAULT_ASSOCIATION_FILTERS,
        ...(this.phenotypeAssociationFilters || {}),
      };
    },
    viewFilters() {
      return normalizeHeatmapViewFilters(this.heatmapViewFilters);
    },
    /** Y-axis is gene set clusters (default Render by). */
    geneSetClustersFilterLocked() {
      return this.cellColorMode === "factorValue" && this.heatmapAxisLayout === "factorsRows";
    },
    /** Y-axis is gene sets. */
    geneSetsFilterLocked() {
      return this.cellColorMode === "factorValue" && this.heatmapAxisLayout === "geneSetsRows";
    },
    effectiveShowGeneSetClusters() {
      return this.geneSetClustersFilterLocked || !!this.viewFilters.showGeneSetClusters;
    },
    effectiveShowGeneSets() {
      return this.geneSetsFilterLocked || !!this.viewFilters.showGeneSets;
    },
    /** True when used inside FactorBaseReveal with factorData / factorDataTableRows passed in. */
    useFactorBaseRevealData() {
      const hasRows = this.factorDataTableRows && this.factorDataTableRows.length > 0;
      const hasFactorData = this.factorData && typeof this.factorData === "object" && Object.keys(this.factorData).length > 0;
      return hasRows || hasFactorData;
    },
    heatmapPairCount() {
      return this.buildHeatmapPairs().length;
    },
    showFactorValueColumnFilters() {
      return (
        !this.loading &&
        this.cellColorMode === "factorValue" &&
        this.useFactorBaseRevealData &&
        this.heatmapPairCount > 0
      );
    },
    /** True when gene columns are classified as search vs context (includedFromRequest). */
    heatmapHasSearchContextGeneColumns() {
      const h = this.heatmapDataFromFactorData;
      return !!(h && h.ready && Array.isArray(h.geneColumnIsSearch) && h.geneColumnIsSearch.length);
    },
    heatmapGroupModeOptions() {
      return [
        { value: "phenotype", text: "Phenotype" },
        { value: "direction", text: "Data category" },
        { value: "all", text: "Show all" },
      ];
    },
    heatmapGroupSelectLabel() {
      if (this.heatmapGroupMode === "direction") return "Data category";
      return "Phenotype";
    },
    /** Active group mode; forced to Show all when Group by UI is hidden. */
    effectiveHeatmapGroupMode() {
      return this.showGroupByControls ? this.heatmapGroupMode : "all";
    },
    pairsInView() {
      const pairs = this.buildHeatmapPairs();
      if (!pairs.length) return [];
      if (this.effectiveHeatmapGroupMode === "all") return pairs.slice();
      const selected = String(this.selectedHeatmapGroup || "").trim();
      if (!selected) return [];
      return this.effectiveHeatmapGroupMode === "direction"
        ? pairs.filter((p) => (p.fetchedDirection || "(unknown direction)") === selected)
        : pairs.filter((p) => p.phenotype === selected);
    },
    dataViewNetwork() {
      if (!this.useFactorBaseRevealData) return { nodes: [], edges: [] };
      const isFactorizationView = this.cellColorMode === "factorValue";
      // Heatmap in factorization mode shows all factors; keep the network in sync
      // (pairsInView is phenotype-group scoped and was dropping other factors).
      let pairs = isFactorizationView ? this.buildHeatmapPairs() : this.pairsInView;
      const vf = this.viewFilters;
      let includeGeneSets = true;
      let includeGenes = true;
      let genesInSearchOnly = false;
      let allowedGeneSets = null;
      let allowedGenes = null;

      if (isFactorizationView) {
        includeGeneSets = this.effectiveShowGeneSets;
        genesInSearchOnly = !!vf.genesInSearchOnly;
        includeGenes = genesInSearchOnly || !!vf.showGenes;

        if (vf.onlySelected) {
          const selected = Array.isArray(this.selectedNodes) ? this.selectedNodes : [];
          if (!selected.length) return { nodes: [], edges: [] };
          const hasRowSel = selected.some(
            (n) =>
              n &&
              (n.kind === HEATMAP_SELECTION_KIND.ROW || n.kind === HEATMAP_SELECTION_KIND.CROSSING)
          );
          const hasColSel = selected.some(
            (n) =>
              n &&
              (n.kind === HEATMAP_SELECTION_KIND.GENE ||
                n.kind === HEATMAP_SELECTION_KIND.GENE_SET ||
                n.kind === HEATMAP_SELECTION_KIND.CROSSING)
          );
          if (hasRowSel) {
            pairs = pairs.filter((p) =>
              isHeatmapRowHighlighted(
                {
                  phenotype: p.phenotype,
                  factor: p.factorObj && p.factorObj.factor,
                  fetchedDirection: p.fetchedDirection,
                },
                selected
              )
            );
          }
          if (hasColSel) {
            allowedGeneSets = new Set();
            allowedGenes = new Set();
            selected.forEach((n) => {
              if (!n) return;
              if (n.kind === HEATMAP_SELECTION_KIND.GENE_SET && n.geneSetId) {
                allowedGeneSets.add(String(n.geneSetId));
              } else if (n.kind === HEATMAP_SELECTION_KIND.GENE && n.gene) {
                allowedGenes.add(String(n.gene));
              } else if (n.kind === HEATMAP_SELECTION_KIND.CROSSING) {
                if (n.colIsGeneSet && n.colLabel) allowedGeneSets.add(String(n.colLabel));
                else if (!n.colIsGeneSet && n.colLabel) allowedGenes.add(String(n.colLabel));
              }
            });
            if (!includeGeneSets) allowedGeneSets = new Set();
            if (!includeGenes) allowedGenes = new Set();
          }
        }
      }

      return buildMergedFactorDataNetwork(pairs, this.factorData || {}, {
        phenotypeDisplay: (id) => this.phenoDisplay(id),
        // Gene-set entry: membership is co-loading only — no gene↔gene-set edges; no phenotype hub.
        linkGenesToGeneSets: !isFactorizationView,
        includePhenotypeNode: !isFactorizationView,
        includeGeneSets,
        includeGenes,
        genesInSearchOnly,
        allowedGeneSets,
        allowedGenes,
      });
    },
    dataViewNetworkGenes() {
      return genesFromFactorDataNetwork(this.dataViewNetwork);
    },
    dataViewNetworkHeight() {
      return 450;
    },
    dataViewNetworkKey() {
      const vf = this.viewFilters;
      const selKey = (this.selectedNodes || [])
        .map((n) => (n && n.key) || "")
        .filter(Boolean)
        .sort()
        .join(",");
      return [
        this.effectiveHeatmapGroupMode,
        this.selectedHeatmapGroup,
        this.pairsInView.length,
        (this.dataViewNetwork.nodes || []).length,
        this.effectiveShowGeneSetClusters ? 1 : 0,
        this.effectiveShowGeneSets ? 1 : 0,
        vf.showGenes ? 1 : 0,
        vf.genesInSearchOnly ? 1 : 0,
        vf.onlySelected ? 1 : 0,
        this.heatmapAxisLayout,
        selKey,
      ].join("|");
    },
    heatmapNodeMenuIsSelected() {
      const node = this.heatmapNodeMenuTarget && this.heatmapNodeMenuTarget.node;
      if (!node) return false;
      return isSelectionNodeSelected(this.selectedNodes, node);
    },
    /** Sorted selector options for the active group-by mode. */
    heatmapGroupOptions() {
      const pairs = this.buildHeatmapPairs();
      if (!pairs.length) return [];
      if (this.effectiveHeatmapGroupMode === "all") return [];
      if (this.effectiveHeatmapGroupMode === "direction") {
        const seen = new Set();
        const dirs = [];
        pairs.forEach((p) => {
          const d = p.fetchedDirection || "(unknown direction)";
          if (!seen.has(d)) {
            seen.add(d);
            dirs.push(d);
          }
        });
        dirs.sort((a, b) => String(a).localeCompare(String(b)));
        return dirs.map((text) => ({ value: text, text }));
      }
      const byPheno = new Map();
      pairs.forEach((p) => {
        if (!byPheno.has(p.phenotype)) byPheno.set(p.phenotype, p.phenotypeDisplay);
      });
      return Array.from(byPheno.entries())
        .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
        .map(([value, text]) => ({ value, text }));
    },
    /**
     * CFDE-REVEAL heatmap for the selected phenotype, data category, or all rows.
     * Columns = gene sets then genes for the visible rows only.
     */
    heatmapDataFromFactorData() {
      const useFactorValue = this.cellColorMode === "factorValue";
      const showScoreHeader = this.showAssociationScoreHeader !== false && !useFactorValue;
      const out = {
        ready: false,
        columnLabels: [],
        columnDisplayLabels: [],
        geneSetCount: 0,
        factorColumnCount: 0,
        factorColumnMetas: [],
        factorRows: [],
        data: [],
        geneScoresForHeader: [],
        specialRowLabels: showScoreHeader
          ? ["Combined score", "GWAS support", "Gene set support"]
          : [],
        cellColorMode: useFactorValue ? "factorValue" : "association",
        valueScaleMax: 1,
        axisLayout: "factorsRows",
      };
      const pairs = this.buildHeatmapPairs();
      if (!pairs.length) return out;

      let itemsInView;
      if (useFactorValue || this.effectiveHeatmapGroupMode === "all") {
        itemsInView = pairs.slice();
      } else {
        const selected = String(this.selectedHeatmapGroup || "").trim();
        if (!selected) return out;
        itemsInView =
          this.effectiveHeatmapGroupMode === "direction"
            ? pairs.filter((p) => (p.fetchedDirection || "(unknown direction)") === selected)
            : pairs.filter((p) => p.phenotype === selected);
      }
      if (!itemsInView.length) return out;

      const orderedPairs = [...itemsInView].sort((a, b) => {
        if (useFactorValue) {
          // Stable Factor0, Factor1, … order when available.
          return String(a.factorObj && a.factorObj.factor || "").localeCompare(
            String(b.factorObj && b.factorObj.factor || ""),
            undefined,
            { numeric: true }
          );
        }
        if (this.effectiveHeatmapGroupMode === "all") {
          const pCmp = a.phenotypeDisplay.localeCompare(b.phenotypeDisplay);
          if (pCmp !== 0) return pCmp;
          return String(a.fetchedDirection || "").localeCompare(String(b.fetchedDirection || ""));
        }
        if (this.effectiveHeatmapGroupMode === "direction") {
          return a.phenotypeDisplay.localeCompare(b.phenotypeDisplay);
        }
        const dirCmp = String(a.fetchedDirection || "").localeCompare(String(b.fetchedDirection || ""));
        if (dirCmp !== 0) return dirCmp;
        return a.phenotypeDisplay.localeCompare(b.phenotypeDisplay);
      });

      const factorData = this.factorData || {};
      const phenotypeIdsForView = [];
      const phenoSeen = new Set();
      orderedPairs.forEach((p) => {
        if (!phenoSeen.has(p.phenotype)) {
          phenoSeen.add(p.phenotype);
          phenotypeIdsForView.push(p.phenotype);
        }
      });

      const geneSetMaxAbs = {};
      const geneSetPValue = {};
      const geneSetOrder = [];
      const geneSetSeen = new Set();
      orderedPairs.forEach(({ factorObj }) => {
        const topStr = factorObj.top_gene_sets;
        if (typeof topStr === "string" && topStr) {
          topStr.split(";").map((s) => s.trim()).filter(Boolean).forEach((id) => {
            if (!geneSetSeen.has(id)) {
              geneSetSeen.add(id);
              geneSetOrder.push(id);
            }
            const gsMeta = factorObj.geneSets && factorObj.geneSets[id];
            const fv =
              gsMeta && gsMeta.factor_value != null && !isNaN(Number(gsMeta.factor_value))
                ? Math.abs(Number(gsMeta.factor_value))
                : 0;
            geneSetMaxAbs[id] = Math.max(geneSetMaxAbs[id] || 0, fv);
            const p =
              gsMeta && gsMeta.p_value != null && !isNaN(Number(gsMeta.p_value))
                ? Number(gsMeta.p_value)
                : null;
            if (p != null && (geneSetPValue[id] == null || p < geneSetPValue[id])) {
              geneSetPValue[id] = p;
            }
          });
        }
      });
      const FACTORIZATION_MIN_GENE_SCORE = 0.01;
      const FACTORIZATION_MAX_GENE_SET_P = 0.05;
      // Gene sets: keep enrichment p < 0.05 (gene_set_score on nodes is -log10(p)).
      const filteredGeneSetOrder = useFactorValue
        ? geneSetOrder.filter((id) => {
            const p = geneSetPValue[id];
            return p != null && !Number.isNaN(p) && p >= 0 && p < FACTORIZATION_MAX_GENE_SET_P;
          })
        : geneSetOrder;
      if (useFactorValue) {
        filteredGeneSetOrder.sort(
          (a, b) => (geneSetMaxAbs[b] || 0) - (geneSetMaxAbs[a] || 0) || String(a).localeCompare(String(b))
        );
      }

      const geneSeen = new Set();
      const geneIsSearch = {};
      const geneMaxAbs = {};
      const geneScoreAbs = {};
      orderedPairs.forEach(({ factorObj }) => {
        const genes = factorObj.genes || {};
        Object.keys(genes).forEach((g) => {
          geneSeen.add(g);
          if (genes[g] && genes[g].includedFromRequest === true) geneIsSearch[g] = true;
          const raw = genes[g] && (genes[g].factor_value ?? genes[g].factorRelevance);
          const fv = raw != null && !isNaN(Number(raw)) ? Math.abs(Number(raw)) : 0;
          geneMaxAbs[g] = Math.max(geneMaxAbs[g] || 0, fv);
          const gs =
            genes[g] && genes[g].gene_score != null && !isNaN(Number(genes[g].gene_score))
              ? Math.abs(Number(genes[g].gene_score))
              : null;
          if (gs != null) geneScoreAbs[g] = Math.max(geneScoreAbs[g] || 0, gs);
        });
      });

      const mergeGeneScoresAcrossPhenotypes = (geneId) => {
        let combined = null;
        let gwasSupport = null;
        let geneSetSupport = null;
        for (let i = 0; i < phenotypeIdsForView.length; i++) {
          const pid = phenotypeIdsForView[i];
          const g = factorData[pid].genes && factorData[pid].genes[geneId];
          if (!g) continue;
          if (g.combined != null && !isNaN(Number(g.combined))) {
            const v = Number(g.combined);
            combined = combined == null ? v : Math.max(combined, v);
          }
          if (g.gwasSupport != null && !isNaN(Number(g.gwasSupport))) {
            const v = Number(g.gwasSupport);
            gwasSupport = gwasSupport == null ? v : Math.max(gwasSupport, v);
          }
          if (g.geneSetSupport != null && !isNaN(Number(g.geneSetSupport))) {
            const v = Number(g.geneSetSupport);
            geneSetSupport = geneSetSupport == null ? v : Math.max(geneSetSupport, v);
          }
        }
        return { combined, gwasSupport, geneSetSupport };
      };

      const filters = this.associationFilters;
      // Search genes first (bold), then context genes; optionally drop by Combined tier.
      let geneOrder = Array.from(geneSeen)
        .filter((g) => {
          if (useFactorValue) {
            // Threshold is on gene_score only — not Overall factor value.
            return (geneScoreAbs[g] || 0) >= FACTORIZATION_MIN_GENE_SCORE;
          }
          return associationTierPasses(mergeGeneScoresAcrossPhenotypes(g).combined, filters);
        })
        .sort((a, b) => {
          const aSearch = geneIsSearch[a] ? 0 : 1;
          const bSearch = geneIsSearch[b] ? 0 : 1;
          if (aSearch !== bSearch) return aSearch - bSearch;
          if (useFactorValue) {
            const d = (geneMaxAbs[b] || 0) - (geneMaxAbs[a] || 0);
            if (d !== 0) return d;
          }
          return String(a).localeCompare(String(b));
        });

      let visibleGeneSets = filteredGeneSetOrder.slice();
      let visibleGenes = geneOrder.slice();
      if (useFactorValue) {
        const vf = this.viewFilters;
        if (!this.effectiveShowGeneSets) visibleGeneSets = [];
        if (vf.genesInSearchOnly) {
          visibleGenes = geneOrder.filter((g) => !!geneIsSearch[g]);
        } else if (!vf.showGenes) {
          visibleGenes = [];
        }
      }

      let factorRows = orderedPairs.map((p) => ({
        phenotype: p.phenotype,
        phenotypeDisplay: p.phenotypeDisplay,
        fetchedDirection: p.fetchedDirection,
        factorClusterLabel: p.factorClusterLabel,
        factor: p.factorObj.factor,
        factorObj: p.factorObj,
        rowKind: "factor",
      }));

      if (useFactorValue && this.viewFilters.onlySelected) {
        const selected = Array.isArray(this.selectedNodes) ? this.selectedNodes : [];
        if (!selected.length) {
          return out;
        }
        const hasRowSel = selected.some(
          (n) =>
            n &&
            (n.kind === HEATMAP_SELECTION_KIND.ROW || n.kind === HEATMAP_SELECTION_KIND.CROSSING)
        );
        const hasColSel = selected.some(
          (n) =>
            n &&
            (n.kind === HEATMAP_SELECTION_KIND.GENE ||
              n.kind === HEATMAP_SELECTION_KIND.GENE_SET ||
              n.kind === HEATMAP_SELECTION_KIND.CROSSING)
        );
        if (hasRowSel) {
          factorRows = factorRows.filter((row) => isHeatmapRowHighlighted(row, selected));
        }
        if (hasColSel) {
          visibleGeneSets = visibleGeneSets.filter((id) =>
            isHeatmapColHighlighted(id, 0, 1, selected)
          );
          visibleGenes = visibleGenes.filter((g) => isHeatmapColHighlighted(g, 1, 0, selected));
        }
      }

      const useGeneSetsRows =
        useFactorValue && this.heatmapAxisLayout === "geneSetsRows";

      if (useGeneSetsRows) {
        // Gene sets (Y) × factors & genes (X). Selection filters already applied above.
        const selected = Array.isArray(this.selectedNodes) ? this.selectedNodes : [];
        let bodyRows = visibleGeneSets.map((geneSetId) => ({
          rowKind: "geneSet",
          geneSetId,
          phenotype: "",
          phenotypeDisplay: geneSetId,
          fetchedDirection: "",
          factorClusterLabel: geneSetId,
          factor: "",
          factorObj: null,
        }));
        // Keep full factorRows for membership lookups; checkbox only hides cluster columns.
        let factorCols = this.effectiveShowGeneSetClusters ? factorRows.slice() : [];
        let geneCols = visibleGenes.slice();
        const membershipFactors = factorRows.slice();

        if (this.viewFilters.onlySelected && selected.length) {
          const hasGsRowSel = selected.some(
            (n) =>
              n &&
              (n.kind === HEATMAP_SELECTION_KIND.GENE_SET ||
                (n.kind === HEATMAP_SELECTION_KIND.CROSSING && n.colIsGeneSet))
          );
          const hasFactorColSel = selected.some(
            (n) =>
              n &&
              (n.kind === HEATMAP_SELECTION_KIND.ROW ||
                (n.kind === HEATMAP_SELECTION_KIND.CROSSING && n.colIsGeneSet))
          );
          const hasGeneColSel = selected.some(
            (n) =>
              n &&
              (n.kind === HEATMAP_SELECTION_KIND.GENE ||
                (n.kind === HEATMAP_SELECTION_KIND.CROSSING && !n.colIsGeneSet))
          );
          if (hasGsRowSel) {
            bodyRows = bodyRows.filter((row) =>
              isHeatmapColHighlighted(row.geneSetId, 0, 1, selected)
            );
          }
          if (hasFactorColSel) {
            factorCols = factorCols.filter((row) => isHeatmapRowHighlighted(row, selected));
          }
          if (hasGeneColSel) {
            geneCols = geneCols.filter((g) => isHeatmapColHighlighted(g, 1, 0, selected));
          }
        }

        const factorColumnCount = factorCols.length;
        const columnLabels = [
          ...factorCols.map((r, i) => `factorCol:${i}:${r.phenotype}|${r.factor}|${r.fetchedDirection || ""}`),
          ...geneCols,
        ];
        const columnDisplayLabels = [
          ...factorCols.map(
            (r) =>
              (r.factorClusterLabel && String(r.factorClusterLabel).trim()) ||
              (r.factor != null ? String(r.factor) : "") ||
              r.phenotypeDisplay ||
              ""
          ),
          ...geneCols,
        ];

        if (!bodyRows.length || !columnLabels.length) return out;

        const geneInGeneSetMember = (geneSetId, gene, factorRow) => {
          const gsMeta = factorRow.factorObj && factorRow.factorObj.geneSets && factorRow.factorObj.geneSets[geneSetId];
          if (!gsMeta || !Array.isArray(gsMeta.genes)) return false;
          const upper = String(gene).toUpperCase();
          return gsMeta.genes.some((g) => String(g).toUpperCase() === upper);
        };

        let valueScaleMax = 0;
        const data = bodyRows.map((gsRow) => {
          const geneSetId = gsRow.geneSetId;
          return columnLabels.map((colLabel, c) => {
            if (c < factorColumnCount) {
              const factorRow = factorCols[c];
              const gsMeta =
                factorRow.factorObj &&
                factorRow.factorObj.geneSets &&
                factorRow.factorObj.geneSets[geneSetId];
              if (gsMeta && gsMeta.factor_value != null && !isNaN(Number(gsMeta.factor_value))) {
                const v = Number(gsMeta.factor_value);
                valueScaleMax = Math.max(valueScaleMax, Math.abs(v));
                return v;
              }
              return null;
            }
            const gene = colLabel;
            let best = null;
            membershipFactors.forEach((factorRow) => {
              if (!geneInGeneSetMember(geneSetId, gene, factorRow)) return;
              const geneData =
                factorRow.factorObj && factorRow.factorObj.genes && factorRow.factorObj.genes[gene];
              const raw = geneData && (geneData.factor_value ?? geneData.factorRelevance);
              if (raw != null && raw !== "" && !isNaN(Number(raw))) {
                const v = Number(raw);
                if (best == null || Math.abs(v) > Math.abs(best)) best = v;
              } else if (best == null) {
                best = 0;
              }
            });
            if (best != null) valueScaleMax = Math.max(valueScaleMax, Math.abs(best));
            return best;
          });
        });

        out.axisLayout = "geneSetsRows";
        out.columnLabels = columnLabels;
        out.columnDisplayLabels = columnDisplayLabels;
        out.geneSetCount = 0;
        out.factorColumnCount = factorColumnCount;
        out.factorColumnMetas = factorCols;
        out.geneColumnIsSearch = geneCols.map((g) => !!geneIsSearch[g]);
        out.factorRows = bodyRows;
        out.geneScoresForHeader = columnLabels.map(() => ({
          combined: null,
          gwasSupport: null,
          geneSetSupport: null,
        }));
        out.data = data;
        out.valueScaleMax = valueScaleMax > 0 ? valueScaleMax : 1;
        out.ready = data.length > 0 && data[0].length > 0;
        return out;
      }

      const columnLabels = [...visibleGeneSets, ...visibleGenes];
      const geneSetCount = visibleGeneSets.length;
      const geneColumnIsSearch = visibleGenes.map((g) => !!geneIsSearch[g]);

      if (columnLabels.length === 0 || factorRows.length === 0) return out;

      const geneScoresForHeader = columnLabels.map((colLabel, c) => {
        if (c < geneSetCount) return { combined: null, gwasSupport: null, geneSetSupport: null };
        return mergeGeneScoresAcrossPhenotypes(colLabel);
      });

      let valueScaleMax = 0;
      const data = factorRows.map((rowInfo) => {
        const factorObj = rowInfo.factorObj;
        return columnLabels.map((colLabel, c) => {
          if (c < geneSetCount) {
            if (useFactorValue) {
              const gsMeta = factorObj.geneSets && factorObj.geneSets[colLabel];
              if (gsMeta && gsMeta.factor_value != null && !isNaN(Number(gsMeta.factor_value))) {
                const v = Number(gsMeta.factor_value);
                valueScaleMax = Math.max(valueScaleMax, Math.abs(v));
                return v;
              }
              return null;
            }
            const topStr = factorObj.top_gene_sets || "";
            const ids = typeof topStr === "string" ? topStr.split(";").map((s) => s.trim()).filter(Boolean) : [];
            return ids.includes(colLabel) ? 1 : 0;
          }
          const gene = colLabel;
          const geneData = (factorObj.genes || {})[gene];
          if (!geneData) return null;
          const raw = geneData.factor_value ?? geneData.factorRelevance;
          if (raw != null && raw !== "" && !isNaN(Number(raw))) {
            const v = Number(raw);
            if (useFactorValue) valueScaleMax = Math.max(valueScaleMax, Math.abs(v));
            return v;
          }
          // Context genes may lack phenotype scores; still mark presence on the factor.
          if (useFactorValue) return null;
          return geneData.includedFromRequest === false ? 1 : null;
        });
      });

      out.axisLayout = "factorsRows";
      out.columnLabels = columnLabels;
      out.columnDisplayLabels = columnLabels.slice();
      out.geneSetCount = geneSetCount;
      out.factorColumnCount = 0;
      out.factorColumnMetas = [];
      out.geneColumnIsSearch = geneColumnIsSearch;
      out.factorRows = factorRows;
      out.geneScoresForHeader = geneScoresForHeader;
      out.data = data;
      out.valueScaleMax = valueScaleMax > 0 ? valueScaleMax : 1;
      out.ready = data.length > 0 && data[0].length > 0;
      return out;
    },
    containerStyle() {
      const base = {
        width: "100%",
        position: "relative",
        background: "#fff",
      };
      if (this.useFactorBaseRevealData && this.dataVizTab === "network") {
        return {
          ...base,
          height: "auto",
          minHeight: "0",
        };
      }

      let dynamicHeight = this.height;
      const cellHeight = 20;
      const margin = { top: 100, bottom: 20 };
      const legendHeight = 60;
      const scrollbarHeight = 30;
      const extraPadding = 70;
      const ledgerChromeHeight = this.useFactorBaseRevealData && this.heatmapPairCount ? 120 : 0;
      const parsedHeight = parseInt(this.height);
      const minHeight = Number.isFinite(parsedHeight) ? parsedHeight : 0;
      const maxHeight = 2000;

      if (this.useFactorBaseRevealData && this.heatmapDataFromFactorData && this.heatmapDataFromFactorData.ready) {
        const scoreHeaderCount = (this.heatmapDataFromFactorData.specialRowLabels || []).length;
        const headerRows = scoreHeaderCount > 0 ? scoreHeaderCount + 1 : 1;
        const rowCellHeight = 24;
        const gridRows = (this.heatmapDataFromFactorData.data && this.heatmapDataFromFactorData.data.length) || 0;
        const totalRows = headerRows + gridRows;
        const calculatedHeight =
          headerRows * cellHeight + gridRows * rowCellHeight + margin.top + margin.bottom + legendHeight + scrollbarHeight + extraPadding + ledgerChromeHeight;
        dynamicHeight = Math.min(Math.max(calculatedHeight, minHeight), maxHeight) + "px";
      } else if (this.heatmapData && this.heatmapData.factors && this.heatmapData.factors.length > 0) {
        const numSpecialRows = 3;
        const numFactors = this.heatmapData.factors.length;
        const totalRows = numSpecialRows + numFactors;
        const calculatedHeight = totalRows * cellHeight + margin.top + margin.bottom + legendHeight + scrollbarHeight + extraPadding;
        dynamicHeight = Math.min(Math.max(calculatedHeight, minHeight), maxHeight) + "px";
      } else if (!Number.isFinite(parsedHeight)) {
        dynamicHeight = "260px";
      }

      if (this.useFactorBaseRevealData && this.heatmapPairCount) {
        return {
          ...base,
          height: "auto",
          minHeight: dynamicHeight,
        };
      }

      return {
        ...base,
        height: dynamicHeight,
      };
    }
  },
  watch: {
    heatmapData: {
      handler() {
        this.cleanupTooltips();
        if (this.useFactorBaseRevealData) return;
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderHeatmap();
          }, 100);
        });
      },
      deep: true,
      immediate: true
    },
    factorData: {
      handler() {
        if (!this.useFactorBaseRevealData || !this.heatmapDataFromFactorData || !this.heatmapDataFromFactorData.ready) return;
        this.cleanupTooltips();
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderFactorBaseRevealHeatmap();
          }, 100);
        });
      },
      deep: true,
      immediate: true
    },
    factorDataTableRows: {
      handler() {
        if (!this.useFactorBaseRevealData || !this.heatmapDataFromFactorData || !this.heatmapDataFromFactorData.ready) return;
        this.cleanupTooltips();
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderFactorBaseRevealHeatmap();
          }, 100);
        });
      },
      deep: true,
      immediate: true
    },
    rowLabelMode() {
      if (!this.useFactorBaseRevealData || !this.heatmapDataFromFactorData || !this.heatmapDataFromFactorData.ready) return;
      this.cleanupTooltips();
      this.$nextTick(() => {
        setTimeout(() => this.renderFactorBaseRevealHeatmap(), 50);
      });
    },
    heatmapAxisLayout() {
      if (!this.useFactorBaseRevealData || this.cellColorMode !== "factorValue") return;
      this.cleanupTooltips();
      this.$nextTick(() => {
        setTimeout(() => this.renderFactorBaseRevealHeatmap(), 50);
      });
    },
    phenotypeAssociationFilters: {
      handler() {
        if (!this.useFactorBaseRevealData) return;
        this.cleanupTooltips();
        this.$nextTick(() => {
          setTimeout(() => this.renderFactorBaseRevealHeatmap(), 50);
        });
      },
      deep: true,
    },
    heatmapGroupOptions: {
      handler(opts) {
        if (this.heatmapGroupMode === "all") {
          this.selectedHeatmapGroup = "";
          return;
        }
        if (!opts || !opts.length) {
          this.selectedHeatmapGroup = "";
          return;
        }
        const valid = opts.some((o) => o.value === this.selectedHeatmapGroup);
        if (!this.selectedHeatmapGroup || !valid) {
          this.selectedHeatmapGroup = opts[0].value;
        }
      },
      immediate: true
    },
    heatmapGroupMode() {
      this.$nextTick(() => {
        if (this.heatmapGroupMode === "all") {
          this.selectedHeatmapGroup = "";
        } else {
          const opts = this.heatmapGroupOptions;
          if (opts && opts.length) {
            this.selectedHeatmapGroup = opts[0].value;
          } else {
            this.selectedHeatmapGroup = "";
          }
        }
        if (!this.useFactorBaseRevealData) return;
        this.cleanupTooltips();
        setTimeout(() => {
          if (this.heatmapDataFromFactorData && this.heatmapDataFromFactorData.ready) {
            this.renderFactorBaseRevealHeatmap();
          }
        }, 100);
      });
    },
    selectedHeatmapGroup() {
      if (!this.useFactorBaseRevealData) return;
      this.cleanupTooltips();
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.heatmapDataFromFactorData && this.heatmapDataFromFactorData.ready) {
            this.renderFactorBaseRevealHeatmap();
          }
        }, 100);
      });
    },
    geneSetsHeatmapData: {
      handler(newVal, oldVal) {
        console.log('geneSetsHeatmapData watcher triggered', { 
          newVal, 
          hasGeneSets: newVal && newVal.geneSets && newVal.geneSets.length > 0,
          geneSetsLength: newVal && newVal.geneSets ? newVal.geneSets.length : 0
        });
        // Clean up any orphaned tooltips before rendering
        this.cleanupTooltips();
        if (newVal && newVal.geneSets && newVal.geneSets.length > 0) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.renderGeneSetsHeatmap();
            }, 300);
          });
        }
      },
      deep: true,
      immediate: true
    },
    dataVizTab(next) {
      if (next === "network" && (this.dataViewNetwork.nodes || []).length) {
        this.beginDataViewNetworkRender();
      }
      // Heatmap SVG can be cleared while the pane is display:none (e.g. selection
      // updates during network view). Re-draw when returning to the heatmap tab.
      if (next === "heatmap" && this.useFactorBaseRevealData) {
        this.scheduleFactorBaseHeatmapRerender();
      }
    },
    dataViewNetworkKey() {
      if (this.dataVizTab === "network" && (this.dataViewNetwork.nodes || []).length) {
        this.beginDataViewNetworkRender();
      }
    },
    heatmapDataFromFactorData: {
      handler(val) {
        if (!this.useFactorBaseRevealData) return;
        this.cleanupTooltips();
        this.$nextTick(() => {
          setTimeout(() => {
            if (val && val.ready) {
              this.renderFactorBaseRevealHeatmap();
            } else {
              this.clearFactorBaseHeatmapDom();
            }
          }, 100);
        });
      },
      deep: true,
    },
    heatmapPairCount(count) {
      if (!this.useFactorBaseRevealData || !count) return;
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.heatmapDataFromFactorData && this.heatmapDataFromFactorData.ready) {
            this.renderFactorBaseRevealHeatmap();
          }
        }, 100);
      });
    },
    selectedNodes: {
      handler() {
        if (!this.useFactorBaseRevealData) return;
        // Selection highlight always needs a redraw; Only selected also changes matrix shape.
        this.scheduleFactorBaseHeatmapRerender();
      },
      deep: true,
    },
    heatmapViewFilters: {
      handler() {
        if (!this.useFactorBaseRevealData || this.cellColorMode !== "factorValue") return;
        this.scheduleFactorBaseHeatmapRerender();
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.useFactorBaseRevealData && this.heatmapDataFromFactorData && this.heatmapDataFromFactorData.ready) {
        setTimeout(() => {
          this.renderFactorBaseRevealHeatmap();
        }, 200);
        return;
      }
      if (this.heatmapData && this.heatmapData.genes && this.heatmapData.genes.length > 0) {
        setTimeout(() => {
          this.renderHeatmap();
        }, 200);
      }
      if (this.geneSetsHeatmapData && this.geneSetsHeatmapData.geneSets && this.geneSetsHeatmapData.geneSets.length > 0) {
        setTimeout(() => {
          this.renderGeneSetsHeatmap();
        }, 400);
      }
    });
  },
  beforeDestroy() {
    this.clearDataViewNetworkRenderTimer();
    // Clean up all tooltips when component is destroyed
    this.cleanupTooltips();
  },
  methods: {
    scheduleFactorBaseHeatmapRerender() {
      if (!this.useFactorBaseRevealData) return;
      // Avoid clearing/redrawing into a display:none pane (network tab); redraw on tab return.
      if (this.dataVizTab === "network") return;
      this.cleanupTooltips();
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.heatmapDataFromFactorData && this.heatmapDataFromFactorData.ready) {
            this.renderFactorBaseRevealHeatmap();
          } else {
            this.clearFactorBaseHeatmapDom();
          }
        }, 50);
      });
    },
    clearFactorBaseHeatmapDom() {
      if (this.$refs.heatmapContainer) this.$refs.heatmapContainer.innerHTML = "";
      if (this.$refs.heatmapLabelsContainer) this.$refs.heatmapLabelsContainer.innerHTML = "";
    },
    beginDataViewNetworkRender() {
      this.clearDataViewNetworkRenderTimer();
      this.dataVizNetworkRendering = true;
      this.dataVizNetworkRenderTimerId = setTimeout(() => {
        this.dataVizNetworkRendering = false;
        this.dataVizNetworkRenderTimerId = null;
      }, 15000);
    },
    onDataViewNetworkReady() {
      this.clearDataViewNetworkRenderTimer();
      this.dataVizNetworkRendering = false;
    },
    clearDataViewNetworkRenderTimer() {
      if (this.dataVizNetworkRenderTimerId != null) {
        clearTimeout(this.dataVizNetworkRenderTimerId);
        this.dataVizNetworkRenderTimerId = null;
      }
    },
    onAssociationFilterChange(tierId, checked) {
      const next = {
        ...DEFAULT_ASSOCIATION_FILTERS,
        ...(this.phenotypeAssociationFilters || {}),
        [tierId]: !!checked,
      };
      this.$emit("update:phenotypeAssociationFilters", next);
    },
    onHeatmapViewFilterChange(key, checked) {
      const next = {
        ...normalizeHeatmapViewFilters(this.heatmapViewFilters),
        [key]: !!checked,
      };
      this.$emit("update:heatmapViewFilters", next);
    },
    phenoDisplay(pid) {
      const descById = this.phenotypeDescriptionById || {};
      const fromSearch = descById[pid] != null && String(descById[pid]).trim() !== "" ? String(descById[pid]).trim() : "";
      const fromCfde = resolveCfdePhenotypeLabel(pid);
      return fromSearch || fromCfde || pid;
    },
    resolveFactorObjFromTableRow(row) {
      const phenotype = row && row.phenotype;
      const factorId = row && row.factor != null ? String(row.factor) : "";
      const direction =
        row && (row.fetched_direction || row.fetchDirection || row.route_category)
          ? String(row.fetched_direction || row.fetchDirection || row.route_category).trim()
          : "";
      const data = (this.factorData || {})[phenotype];
      if (!data || !factorId) return null;
      const factors = data.factors || data.allFactors || [];
      const exact = factors.find((f) => {
        const fDir =
          f.fetched_direction != null && String(f.fetched_direction).trim() !== ""
            ? String(f.fetched_direction).trim()
            : (f.route_category != null ? String(f.route_category).trim() : "");
        const fId = f.factor != null ? String(f.factor) : "";
        return fId === factorId && (!direction || fDir === direction);
      });
      if (exact) return exact;
      return factors.find((f) => (f.factor != null ? String(f.factor) : "") === factorId) || null;
    },
    buildHeatmapPairs() {
      const factorData = this.factorData || {};
      const tableRows = Array.isArray(this.factorDataTableRows) ? this.factorDataTableRows : [];
      const pairs = [];
      const pushPair = (phenotype, factorObj) => {
        if (!phenotype || !factorObj) return;
        const clusterKey =
          factorObj.label != null && String(factorObj.label).trim() !== ""
            ? String(factorObj.label).trim()
            : factorObj.factor != null
              ? String(factorObj.factor)
              : "";
        const fetchedDirection =
          factorObj.fetched_direction != null && String(factorObj.fetched_direction).trim() !== ""
            ? String(factorObj.fetched_direction).trim()
            : (factorObj.route_category != null && String(factorObj.route_category).trim() !== ""
                ? String(factorObj.route_category).trim()
                : "");
        pairs.push({
          phenotype,
          phenotypeDisplay: this.phenoDisplay(phenotype),
          factorClusterLabel: resolveCfdeFactorClusterDisplayLabel(clusterKey),
          factorObj,
          fetchedDirection,
        });
      };

      if (tableRows.length) {
        tableRows.forEach((row) => {
          const factorObj = this.resolveFactorObjFromTableRow(row);
          pushPair(row.phenotype, factorObj);
        });
        return pairs.filter((p) => p.factorObj);
      }

      Object.keys(factorData)
        .filter((k) => factorData[k] && (factorData[k].factors || []).length > 0)
        .forEach((phenotype) => {
          (factorData[phenotype].factors || []).forEach((f) => pushPair(phenotype, f));
        });
      return pairs;
    },
    closeHeatmapNodeMenu() {
      this.heatmapNodeMenuOpen = false;
      this.heatmapNodeMenuTarget = null;
    },
    openHeatmapNodeMenu(event, node) {
      if (!node) return;
      if (event && typeof event.preventDefault === "function") event.preventDefault();
      if (event && typeof event.stopPropagation === "function") event.stopPropagation();
      this.heatmapNodeMenuTarget = { node };
      this.heatmapNodeMenuLeft = event && event.clientX != null ? event.clientX : 0;
      this.heatmapNodeMenuTop = event && event.clientY != null ? event.clientY : 0;
      this.heatmapNodeMenuOpen = true;
    },
    onHeatmapNodeMenuToggle(target) {
      const node = target && target.node;
      if (!node) return;
      const next = toggleSelectionNode(this.selectedNodes, node);
      this.$emit("update:selectedNodes", next);
    },
    onRemoveSelectedNode(key) {
      const next = removeSelectionNode(this.selectedNodes, key);
      this.$emit("update:selectedNodes", next);
    },
    applyHeatmapCrossingCellHighlight(rect, highlighted) {
      if (!rect || !highlighted) return;
      rect.setAttribute("fill", "#ff6600");
      rect.setAttribute("stroke", "#fff");
      rect.setAttribute("stroke-width", "1");
    },
    applyHeatmapRowLabelHighlight(textEl, highlighted) {
      if (!textEl) return;
      if (highlighted) {
        textEl.classList.add("heatmap-label-selected");
      } else {
        textEl.classList.remove("heatmap-label-selected");
      }
    },
    applyHeatmapColLabelHighlight(textEl, highlighted) {
      this.applyHeatmapRowLabelHighlight(textEl, highlighted);
    },
    attachHeatmapSelectClick(el, node) {
      if (!el || !node) return;
      el.style.cursor = "pointer";
      el.addEventListener("click", (event) => this.openHeatmapNodeMenu(event, node));
    },
    cleanupTooltips() {
      // Remove any orphaned tooltips from the DOM
      const existingTooltips = document.querySelectorAll('.heatmap-tooltip');
      existingTooltips.forEach(tooltip => {
        try {
          if (tooltip.parentNode) {
            tooltip.parentNode.removeChild(tooltip);
          }
        } catch (e) {
          // Tooltip already removed
        }
      });
    },
    /**
     * Renders CFDE-REVEAL heatmap: row labels from phenotype and/or data category;
     * columns = gene sets then genes; score rows + body crossings / gene relevance.
     */
    renderFactorBaseRevealHeatmap() {
      const h = this.heatmapDataFromFactorData;
      if (!h || !h.ready || !h.data.length) return;
      if (!this.$refs.heatmapContainer || !this.$refs.heatmapLabelsContainer) return;
      // Pane is hidden on the network tab; redraw when switching back instead.
      if (this.useFactorBaseRevealData && this.dataVizTab === "network") return;

      this.cleanupTooltips();
      this.$refs.heatmapContainer.innerHTML = "";
      this.$refs.heatmapLabelsContainer.innerHTML = "";

      const {
        columnLabels,
        columnDisplayLabels,
        geneSetCount,
        factorColumnCount,
        factorColumnMetas,
        factorRows,
        data,
        geneScoresForHeader,
        specialRowLabels,
        geneColumnIsSearch,
        cellColorMode,
        valueScaleMax,
        axisLayout,
      } = h;
      const numCols = columnLabels.length;
      const numFactorRows = data.length;
      const scoreHeaderCount = Array.isArray(specialRowLabels) ? specialRowLabels.length : 0;
      const headerRows = scoreHeaderCount > 0 ? scoreHeaderCount + 1 : 1;
      const cellWidth = Math.max(24, Math.min(40, 400 / Math.max(1, numCols)));
      const cellHeight = 20;
      const cellHeightBody = 24;
      const margin = { top: 80, right: 20, bottom: 20, left: 0 };
      const labelMargin = { top: 80, right: 0, bottom: 20, left: 10 };
      const scrollableWidth = Math.max(500, numCols * cellWidth + margin.left + margin.right);
      const totalContentHeight = headerRows * cellHeight + numFactorRows * cellHeightBody;
      const height = totalContentHeight + margin.top + margin.bottom;
      const searchFlags = Array.isArray(geneColumnIsSearch) ? geneColumnIsSearch : [];
      const useFactorValueColors = cellColorMode === "factorValue";
      const scaleMax = valueScaleMax > 0 ? valueScaleMax : 1;
      const isGeneSetsRows = axisLayout === "geneSetsRows";
      const factorColCount = Number(factorColumnCount) || 0;
      const displayLabels =
        Array.isArray(columnDisplayLabels) && columnDisplayLabels.length === columnLabels.length
          ? columnDisplayLabels
          : columnLabels;
      const factorMetas = Array.isArray(factorColumnMetas) ? factorColumnMetas : [];

      const getRelevanceColor = (value) => {
        if (value === null || value === undefined || isNaN(value)) return "#f0f0f0";
        const abs = Math.abs(Number(value));
        const v = useFactorValueColors
          ? Math.max(0, Math.min(1, abs / scaleMax))
          : Math.max(0, Math.min(1, Number(value)));
        const r = Math.round(243 + (156 - 243) * v);
        const g = Math.round(229 + (39 - 229) * v);
        const b = Math.round(245 + (176 - 245) * v);
        return `rgb(${r},${g},${b})`;
      };
      const getBinaryColor = (value) => (value === 1 ? "rgb(156, 39, 176)" : "#f0f0f0");
      const getCellColor = (value, isGeneSetCol) => {
        if (useFactorValueColors) return getRelevanceColor(value);
        return isGeneSetCol ? getBinaryColor(value) : getRelevanceColor(value);
      };

      const labelsSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      const labelWidthNoRelevance = 360;
      labelsSvg.setAttribute("width", labelWidthNoRelevance);
      labelsSvg.setAttribute("height", height);
      labelsSvg.setAttribute("class", "heatmap-labels-svg");

      const labelsG = document.createElementNS("http://www.w3.org/2000/svg", "g");
      labelsG.setAttribute("transform", `translate(10,${labelMargin.top})`);
      specialRowLabels.forEach((label, rowIndex) => {
        const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
        text.setAttribute("x", labelWidthNoRelevance - 10);
        text.setAttribute("y", (1 + rowIndex) * cellHeight + cellHeight / 2);
        text.setAttribute("text-anchor", "end");
        text.setAttribute("alignment-baseline", "middle");
        text.setAttribute("class", "heatmap-label heatmap-label-score-row");
        text.setAttribute("font-size", "10pt");
        text.setAttribute("font-family", "Arial");
        text.textContent = label;
        labelsG.appendChild(text);
      });
      const textX = labelWidthNoRelevance - 10;
      factorRows.forEach((rowMeta, rowIndex) => {
        const rowTop = headerRows * cellHeight + rowIndex * cellHeightBody;
        const rowMid = rowTop + cellHeightBody / 2;
        const phen = document.createElementNS("http://www.w3.org/2000/svg", "text");
        phen.setAttribute("x", textX);
        phen.setAttribute("y", rowMid);
        phen.setAttribute("text-anchor", "end");
        phen.setAttribute("alignment-baseline", "middle");
        phen.setAttribute("class", "heatmap-label");
        phen.setAttribute("font-size", "10pt");
        phen.setAttribute("font-family", "Arial");
        const pFull = rowMeta.phenotypeDisplay;
        const showDirPrefix =
          this.showDirectionInRowLabels &&
          !isGeneSetsRows &&
          this.rowLabelMode !== "factor" &&
          (this.effectiveHeatmapGroupMode === "phenotype" || this.effectiveHeatmapGroupMode === "all") &&
          rowMeta.fetchedDirection;
        const directionTag = showDirPrefix ? `[${rowMeta.fetchedDirection}] ` : "";
        const rowDisplay = isGeneSetsRows
          ? rowMeta.geneSetId || rowMeta.phenotypeDisplay || ""
          : this.heatmapRowDisplayLabel(rowMeta);
        const fullLabel = `${directionTag}${rowDisplay}`;
        phen.textContent = this.truncateLabel(fullLabel, 52);
        const tooltipParts = [];
        if (isGeneSetsRows) {
          tooltipParts.push(`Gene set: ${rowMeta.geneSetId || fullLabel}`);
        } else {
          if (this.showDirectionInRowLabels && this.rowLabelMode !== "factor" && rowMeta.fetchedDirection) {
            tooltipParts.push(`Data category: ${rowMeta.fetchedDirection}`);
          }
          if (rowMeta.factorClusterLabel) tooltipParts.push(`Gene set cluster: ${rowMeta.factorClusterLabel}`);
          if (this.rowLabelMode !== "factor") tooltipParts.push(`Phenotype: ${pFull}`);
        }
        phen.setAttribute("title", tooltipParts.join(" · ") || fullLabel);
        const rowNode = isGeneSetsRows
          ? buildGeneSetSelectionNode(rowMeta.geneSetId)
          : buildRowSelectionNode(rowMeta);
        const rowHighlighted = isGeneSetsRows
          ? isHeatmapColHighlighted(rowMeta.geneSetId, 0, 1, this.selectedNodes)
          : isHeatmapRowHighlighted(rowMeta, this.selectedNodes);
        this.applyHeatmapRowLabelHighlight(phen, rowHighlighted);
        this.attachHeatmapSelectClick(phen, rowNode);
        labelsG.appendChild(phen);
      });
      labelsSvg.appendChild(labelsG);
      this.$refs.heatmapLabelsContainer.appendChild(labelsSvg);

      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("width", scrollableWidth);
      svg.setAttribute("height", height);
      svg.setAttribute("class", "heatmap-svg");
      const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
      g.setAttribute("transform", `translate(${margin.left},${margin.top})`);

      columnLabels.forEach((colLabel, colIndex) => {
        const labelX = colIndex * cellWidth + 2;
        const labelY = 0 + cellHeight / 2;
        const displayLabel = displayLabels[colIndex] || colLabel;
        let colNode;
        let colHighlighted;
        if (isGeneSetsRows) {
          if (colIndex < factorColCount) {
            const factorMeta = factorMetas[colIndex];
            colNode = buildRowSelectionNode(factorMeta);
            colHighlighted = isHeatmapRowHighlighted(factorMeta, this.selectedNodes);
          } else {
            colNode = buildGeneSelectionNode(colLabel);
            colHighlighted = isHeatmapColHighlighted(colLabel, 1, 0, this.selectedNodes);
          }
        } else {
          const isGeneSetCol = colIndex < geneSetCount;
          colNode = isGeneSetCol
            ? buildGeneSetSelectionNode(colLabel)
            : buildGeneSelectionNode(colLabel);
          colHighlighted = isHeatmapColHighlighted(
            colLabel,
            colIndex,
            geneSetCount,
            this.selectedNodes
          );
        }
        const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
        text.setAttribute("x", labelX);
        text.setAttribute("y", labelY);
        text.setAttribute("text-anchor", "start");
        text.setAttribute("alignment-baseline", "middle");
        text.setAttribute("class", "heatmap-label");
        text.setAttribute("font-size", "10pt");
        text.setAttribute("font-family", "Arial");
        const isGeneCol =
          isGeneSetsRows ? colIndex >= factorColCount : colIndex >= geneSetCount;
        if (isGeneCol && this.emphasizeSearchContextGenes) {
          const geneIdx = isGeneSetsRows
            ? colIndex - factorColCount
            : colIndex - geneSetCount;
          if (searchFlags[geneIdx]) {
            text.classList.add("heatmap-label-search-gene");
          } else {
            text.classList.add("heatmap-label-context-gene");
          }
        }
        text.setAttribute("transform", `rotate(-45, ${labelX}, ${labelY})`);
        text.textContent = this.truncateLabel(displayLabel, 14);
        if (String(displayLabel).length > 14) text.setAttribute("title", displayLabel);
        this.applyHeatmapColLabelHighlight(text, colHighlighted);
        this.attachHeatmapSelectClick(text, colNode);
        g.appendChild(text);
      });

      const specialScores = [
        geneScoresForHeader.map((s) => s.combined),
        geneScoresForHeader.map((s) => s.gwasSupport),
        geneScoresForHeader.map((s) => s.geneSetSupport)
      ];
      const phenosInHeatmapRows = new Set(
        factorRows.filter((r) => r && r.phenotype).map((r) => r.phenotype)
      );
      const headerScoreSuffix = phenosInHeatmapRows.size > 1 ? " (max across phenotypes)" : "";

      for (let rowIndex = 0; rowIndex < scoreHeaderCount; rowIndex++) {
        const scores = specialScores[rowIndex];
        const rowLabel = specialRowLabels[rowIndex];
        for (let c = 0; c < numCols; c++) {
          const value = scores[c];
          const x = c * cellWidth;
          const y = (1 + rowIndex) * cellHeight;
          const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
          rect.setAttribute("x", x);
          rect.setAttribute("y", y);
          rect.setAttribute("width", cellWidth);
          rect.setAttribute("height", cellHeight);
          rect.setAttribute("fill", c < geneSetCount ? "#f5f5f5" : "#f5f5f5");
          rect.setAttribute("stroke", "#fff");
          rect.setAttribute("stroke-width", "1");
          g.appendChild(rect);
          if (c >= geneSetCount && value != null && !isNaN(Number(value))) {
            const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
            circle.setAttribute("cx", x + cellWidth / 2);
            circle.setAttribute("cy", y + cellHeight / 2);
            const radius = Math.min(cellWidth, cellHeight) * 0.35;
            circle.setAttribute("r", radius);
            const color = this.getColorByScore(value);
            if (color && color.background) {
              circle.setAttribute("fill", color.background);
              circle.setAttribute("stroke", color.border || color.background);
            }
            const gene = columnLabels[c];
            const showTip = (e) => {
              this.cleanupTooltips();
              const tip = document.createElement("div");
              tip.className = "heatmap-tooltip";
              tip.textContent = `${rowLabel} (${gene}): ${Number(value).toFixed(2)}${headerScoreSuffix}`;
              tip.style.cssText = "position:fixed;background:rgba(0,0,0,0.85);color:#fff;padding:6px 10px;border-radius:4px;font-size:12px;z-index:10000;pointer-events:none;";
              document.body.appendChild(tip);
              const place = (ev) => {
                tip.style.left = ((ev && ev.clientX != null ? ev.clientX : 0) + 10) + "px";
                tip.style.top = ((ev && ev.clientY != null ? ev.clientY : 0) - 10) + "px";
              };
              place(e);
              window.addEventListener("mousemove", place);
              circle._tip = tip;
              circle._place = place;
            };
            const hideTip = () => {
              if (circle._tip && circle._tip.parentNode) circle._tip.parentNode.removeChild(circle._tip);
              if (circle._place) window.removeEventListener("mousemove", circle._place);
            };
            circle.addEventListener("mouseenter", showTip);
            circle.addEventListener("mouseleave", hideTip);
            g.appendChild(circle);
          }
        }
      }

      for (let r = 0; r < numFactorRows; r++) {
        for (let c = 0; c < numCols; c++) {
          const value = data[r][c];
          const x = c * cellWidth;
          const y = headerRows * cellHeight + r * cellHeightBody;
          const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
          rect.setAttribute("x", x);
          rect.setAttribute("y", y);
          rect.setAttribute("width", cellWidth);
          rect.setAttribute("height", cellHeightBody);
          const isGeneSetCol = !isGeneSetsRows && c < geneSetCount;
          rect.setAttribute("fill", getCellColor(value, isGeneSetCol));
          rect.setAttribute("stroke", "#fff");
          rect.setAttribute("stroke-width", "1");
          const rowMeta = factorRows[r];
          const displayCol = displayLabels[c] || columnLabels[c];
          let rowLine;
          let crossingNode;
          let cellHighlighted;
          if (isGeneSetsRows) {
            rowLine = rowMeta.geneSetId || "";
            if (c < factorColCount) {
              const factorMeta = factorMetas[c];
              crossingNode = buildCrossingSelectionNode(factorMeta, rowMeta.geneSetId, true);
              cellHighlighted = isHeatmapCellHighlighted(
                factorMeta,
                rowMeta.geneSetId,
                0,
                1,
                this.selectedNodes
              );
            } else {
              const gene = columnLabels[c];
              // Prefer a factor that contains both for stable crossing identity.
              const factorMeta =
                factorMetas.find((fm) => {
                  const gs =
                    fm.factorObj && fm.factorObj.geneSets && fm.factorObj.geneSets[rowMeta.geneSetId];
                  if (!gs || !Array.isArray(gs.genes)) return false;
                  const upper = String(gene).toUpperCase();
                  return gs.genes.some((g) => String(g).toUpperCase() === upper);
                }) || factorMetas[0];
              crossingNode = factorMeta
                ? buildCrossingSelectionNode(factorMeta, gene, false)
                : buildGeneSelectionNode(gene);
              cellHighlighted = factorMeta
                ? isHeatmapCellHighlighted(factorMeta, gene, 1, 0, this.selectedNodes)
                : isHeatmapColHighlighted(gene, 1, 0, this.selectedNodes);
            }
          } else {
            const showDirInRowLine =
              this.showDirectionInRowLabels &&
              this.rowLabelMode !== "factor" &&
              (this.effectiveHeatmapGroupMode === "phenotype" || this.effectiveHeatmapGroupMode === "all") &&
              rowMeta.fetchedDirection;
            const directionTag = showDirInRowLine ? `${rowMeta.fetchedDirection} · ` : "";
            rowLine = `${directionTag}${this.heatmapRowDisplayLabel(rowMeta)}`;
            const colLabel = columnLabels[c];
            crossingNode = buildCrossingSelectionNode(rowMeta, colLabel, isGeneSetCol);
            cellHighlighted = isHeatmapCellHighlighted(
              rowMeta,
              colLabel,
              c,
              geneSetCount,
              this.selectedNodes
            );
          }
          this.applyHeatmapCrossingCellHighlight(rect, cellHighlighted);
          this.attachHeatmapSelectClick(rect, crossingNode);
          const showTooltip = (event) => {
            this.cleanupTooltips();
            const tip = document.createElement("div");
            tip.className = "heatmap-tooltip";
            if (!useFactorValueColors && isGeneSetCol) {
              tip.textContent = `${rowLine} × ${displayCol}: ${value === 1 ? "Yes" : "No"}`;
            } else {
              tip.textContent =
                value != null
                  ? `${rowLine} × ${displayCol}: ${Number(value).toFixed(3)}`
                  : `${rowLine} × ${displayCol}: —`;
            }
            tip.style.cssText = "position:fixed;background:rgba(0,0,0,0.85);color:#fff;padding:6px 10px;border-radius:4px;font-size:12px;z-index:10000;pointer-events:none;";
            document.body.appendChild(tip);
            const place = (e) => {
              tip.style.left = ((e && e.clientX != null ? e.clientX : 0) + 10) + "px";
              tip.style.top = ((e && e.clientY != null ? e.clientY : 0) - 10) + "px";
            };
            place(event);
            window.addEventListener("mousemove", place);
            rect._tip = tip;
            rect._place = place;
          };
          const hideTooltip = () => {
            if (rect._tip && rect._tip.parentNode) rect._tip.parentNode.removeChild(rect._tip);
            if (rect._place) window.removeEventListener("mousemove", rect._place);
            delete rect._tip;
            delete rect._place;
          };
          rect.addEventListener("mouseenter", showTooltip);
          rect.addEventListener("mouseleave", hideTooltip);
          g.appendChild(rect);
        }
      }

      svg.appendChild(g);
      this.$refs.heatmapContainer.appendChild(svg);
    },
    // Truncate label if too long
    truncateLabel(label, maxLength = 35) {
      if (!label || label.length <= maxLength) {
        return label;
      }
      return label.substring(0, maxLength - 3) + '...';
    },
    /** Visible Y-axis / cell-tooltip row title for one heatmap factor row. */
    heatmapRowDisplayLabel(rowMeta) {
      if (!rowMeta) return "";
      if (this.rowLabelMode === "factor") {
        return (
          (rowMeta.factorClusterLabel && String(rowMeta.factorClusterLabel).trim()) ||
          (rowMeta.factor != null ? String(rowMeta.factor) : "") ||
          rowMeta.phenotypeDisplay ||
          ""
        );
      }
      return rowMeta.phenotypeDisplay || "";
    },
    // Get color based on score using same thresholds as PigeanPhenotype.vue table
    // Very Strong (> 3): #4a90e2, Strongly Suggestive (2-3): #f5a623
    // Nominally Significant (1-2): #f8e71c, Not Significant (< 1): #cccccc
    getColorByScore(score) {
      if (score > 3) {
        // Very Strong - blue
        return { background: "#4a90e2", border: "#2c5aa0" };
      } else if (score >= 2 && score <= 3) {
        // Strongly Suggestive - orange
        return { background: "#f5a623", border: "#d68910" };
      } else if (score >= 1 && score < 2) {
        // Nominally Significant - yellow
        return { background: "#f8e71c", border: "#d4c716" };
      } else {
        // Not Significant - gray
        return { background: "#cccccc", border: "#999999" };
      }
    },
    renderHeatmap() {
      console.log('renderHeatmap called', this.heatmapData);
      
      if (!this.heatmapData || !this.heatmapData.genes || this.heatmapData.genes.length === 0) {
        console.log('No heatmap data or empty genes array');
        return;
      }
      
      if (!this.$refs.heatmapContainer || !this.$refs.heatmapLabelsContainer) {
        console.log('heatmapContainer or heatmapLabelsContainer ref not found');
        return;
      }
      
      // Clear previous heatmap and clean up any orphaned tooltips
      // Remove any tooltips that might still be in the DOM
      const existingTooltips = document.querySelectorAll('.heatmap-tooltip');
      existingTooltips.forEach(tooltip => {
        try {
          if (tooltip.parentNode) {
            tooltip.parentNode.removeChild(tooltip);
          }
        } catch (e) {
          // Tooltip already removed
        }
      });
      
      // Clear previous heatmap
      this.$refs.heatmapContainer.innerHTML = '';
      this.$refs.heatmapLabelsContainer.innerHTML = '';
      
      let { genes, factors, factorLabels, factorScores, data, gwasData, geneSetData, combinedScores, gwasScores, geneSetScores } = this.heatmapData;
      const labels = factorLabels || factors; // Use labels if available, otherwise use factor IDs
      
      // Factors are already sorted by relevance score in PigeanFactors.vue, but ensure we have scores
      const relevanceScores = factorScores || factors.map(() => 0);
      
      console.log('Heatmap data:', { genes: genes.length, factors: factors.length, data: data ? data.length : 0 });
      
      if (genes.length === 0 || factors.length === 0) {
        console.log('Empty genes or factors array');
        return;
      }
      
      if (!data || data.length === 0) {
        console.log('No data array');
        return;
      }
      
      // Sort genes by combined score (descending - bigger first)
      if (combinedScores && combinedScores.length === genes.length) {
        // Create array of indices with scores for sorting
        const geneIndices = genes.map((gene, index) => ({
          gene,
          index,
          score: combinedScores[index] !== null && combinedScores[index] !== undefined ? combinedScores[index] : -Infinity
        }));
        
        // Sort by score descending
        geneIndices.sort((a, b) => b.score - a.score);
        
        // Reorder genes and all related arrays
        const sortedGenes = geneIndices.map(item => item.gene);
        const sortedData = geneIndices.map(item => data[item.index]);
        const sortedGwasData = gwasData ? geneIndices.map(item => gwasData[item.index]) : [];
        const sortedGeneSetData = geneSetData ? geneIndices.map(item => geneSetData[item.index]) : [];
        const sortedCombinedScores = geneIndices.map(item => combinedScores[item.index]);
        const sortedGwasScores = gwasScores ? geneIndices.map(item => gwasScores[item.index]) : [];
        const sortedGeneSetScores = geneSetScores ? geneIndices.map(item => geneSetScores[item.index]) : [];
        
        // Update variables
        genes = sortedGenes;
        data = sortedData;
        gwasData = sortedGwasData;
        geneSetData = sortedGeneSetData;
        combinedScores = sortedCombinedScores;
        gwasScores = sortedGwasScores;
        geneSetScores = sortedGeneSetScores;
      }
      
      // Calculate dimensions (swapped: genes on x-axis, factors on y-axis)
      // Add 3 extra rows for Combined score, GWAS support, and Gene set support
      // Add 1 extra column for factor relevance score in y-axis labels
      const numSpecialRows = 3;
      const relevanceColumnWidth = 20; // Fixed width for relevance score column in y-axis labels
      const cellWidth = Math.max(30, 300 / genes.length); // Dynamic width based on number of genes
      const cellHeight = 20;
      const labelWidth = 200 + relevanceColumnWidth; // Width for fixed factor labels + relevance column
      const margin = { top: 100, right: 20, bottom: 20, left: 0 }; // Increased top margin for x-axis labels and rotated relevance label
      const labelMargin = { top: 100, right: 0, bottom: 20, left: 10 }; // Match top margin - increased to accommodate rotated label
      
      const scrollableWidth = Math.max(600, genes.length * cellWidth + margin.left + margin.right);
      const totalRows = numSpecialRows + factors.length;
      const height = totalRows * cellHeight + margin.top + margin.bottom;
      
      // Background color for cells - using purple gradient (different from network colors)
      const getBackgroundColor = (value) => {
        if (value === null || value === undefined || isNaN(value)) {
          return '#f0f0f0'; // Light gray for missing values
        }
        
        // Clamp value to 0-1 range
        const clampedValue = Math.max(0, Math.min(1, value));
        
        // Create gradient from light purple to dark purple
        const r1 = 243, g1 = 229, b1 = 245; // Light purple
        const r2 = 156, g2 = 39, b2 = 176;  // Dark purple
        
        const r = Math.round(r1 + (r2 - r1) * clampedValue);
        const g = Math.round(g1 + (g2 - g1) * clampedValue);
        const b = Math.round(b1 + (b2 - b1) * clampedValue);
        
        return `rgb(${r}, ${g}, ${b})`;
      };
      
      // Create fixed labels SVG (y-axis labels + relevance column)
      const labelsSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      labelsSvg.setAttribute('width', labelWidth);
      labelsSvg.setAttribute('height', height);
      labelsSvg.setAttribute('class', 'heatmap-labels-svg');
      
      // Group for factor labels (text labels)
      const labelsG = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      labelsG.setAttribute('transform', `translate(${labelWidth - relevanceColumnWidth - 10},${labelMargin.top})`);
      
      // Group for relevance column (cells and header)
      const relevanceG = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      relevanceG.setAttribute('transform', `translate(${labelWidth - relevanceColumnWidth},${labelMargin.top})`);
      
      // Draw relevance column header label (rotated 90 degrees)
      // Position it further up and adjust for rotation to prevent cutoff
      // When rotated -90 degrees, the text extends horizontally, so we need more vertical space
      const relevanceHeaderText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      const relevanceHeaderX = 10;//relevanceColumnWidth / 2;
      const relevanceHeaderY = -60; // Move further up to accommodate rotated text (text extends left when rotated -90)
      relevanceHeaderText.setAttribute('x', relevanceHeaderX);
      relevanceHeaderText.setAttribute('y', relevanceHeaderY);
      relevanceHeaderText.setAttribute('text-anchor', 'middle');
      relevanceHeaderText.setAttribute('alignment-baseline', 'middle');
      relevanceHeaderText.setAttribute('class', 'heatmap-label');
      relevanceHeaderText.setAttribute('font-size', '10px'); // Slightly smaller font to fit better
      relevanceHeaderText.setAttribute('font-weight', '600');
      relevanceHeaderText.setAttribute('font-family', 'Arial');
      // Rotate -90 degrees around the center point
      relevanceHeaderText.setAttribute('transform', `rotate(-90, ${relevanceHeaderX}, ${relevanceHeaderY})`);
      relevanceHeaderText.textContent = 'Relevance to trait';
      relevanceG.appendChild(relevanceHeaderText);
      
      // Draw special row labels (Combined score, GWAS support, Gene set support)
      const specialRowLabels = ['Combined score', 'GWAS support', 'Gene set support'];
      
      // Draw relevance column cells for special rows (empty cells)
      specialRowLabels.forEach((label, rowIndex) => {
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', 0);
        rect.setAttribute('y', rowIndex * cellHeight);
        rect.setAttribute('width', relevanceColumnWidth);
        rect.setAttribute('height', cellHeight);
        rect.setAttribute('fill', '#f5f5f5');
        rect.setAttribute('stroke', '#fff');
        rect.setAttribute('stroke-width', '1');
        rect.setAttribute('class', 'heatmap-cell-bg');
        relevanceG.appendChild(rect);
      });
      
      // Draw relevance column cells for factor rows (color-coded)
      factors.forEach((factor, factorIndex) => {
        const relevanceScore = relevanceScores[factorIndex];
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', 0);
        rect.setAttribute('y', (numSpecialRows + factorIndex) * cellHeight);
        rect.setAttribute('width', relevanceColumnWidth);
        rect.setAttribute('height', cellHeight);
        
        // Color code based on relevance score
        if (relevanceScore !== null && relevanceScore !== undefined) {
          const color = this.getColorByScore(relevanceScore);
          rect.setAttribute('fill', color.background);
          rect.setAttribute('stroke', color.border);
          rect.setAttribute('stroke-width', '1');
          rect.setAttribute('title', `Relevance to trait: ${relevanceScore.toFixed(2)}`);
        } else {
          rect.setAttribute('fill', '#f0f0f0');
          rect.setAttribute('stroke', '#fff');
          rect.setAttribute('stroke-width', '1');
        }
        rect.setAttribute('class', 'heatmap-cell-bg');
        relevanceG.appendChild(rect);
      });
      specialRowLabels.forEach((label, rowIndex) => {
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', 0);
        text.setAttribute('y', rowIndex * cellHeight + cellHeight / 2);
        text.setAttribute('text-anchor', 'end');
        text.setAttribute('alignment-baseline', 'middle');
        text.setAttribute('class', 'heatmap-label');
        text.setAttribute('font-size', '11px');
        text.setAttribute('font-weight', '600'); // Make special rows bold
        text.setAttribute('font-family', 'Arial');
        text.textContent = label;
        labelsG.appendChild(text);
      });
      
      // Draw factor labels (y-axis) in fixed container
      factors.forEach((factor, factorIndex) => {
        const label = labels[factorIndex] || factor;
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', 0);
        text.setAttribute('y', (numSpecialRows + factorIndex) * cellHeight + cellHeight / 2);
        text.setAttribute('text-anchor', 'end');
        text.setAttribute('alignment-baseline', 'middle');
        text.setAttribute('class', 'heatmap-label');
        text.setAttribute('font-size', '11px');
        text.setAttribute('font-family', 'Arial');
        // Show full label without truncation
        text.textContent = label;
        
        // Add tooltip for full label (always show tooltip for better UX)
        if (label.length > 20) {
          const labelGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          labelGroup.setAttribute('class', 'heatmap-label-group');
          
          const showLabelTooltip = (event) => {
            // Remove existing tooltip if any
            if (labelGroup._tooltip) {
              try {
                if (labelGroup._tooltip.parentNode) {
                  labelGroup._tooltip.parentNode.removeChild(labelGroup._tooltip);
                }
              } catch (e) {
                // Tooltip already removed
              }
            }
            
            const tooltip = document.createElement('div');
            tooltip.className = 'heatmap-tooltip';
            tooltip.textContent = label;
            // Add inline styles to ensure visibility
            tooltip.style.position = 'fixed';
            tooltip.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
            tooltip.style.color = '#fff';
            tooltip.style.padding = '6px 10px';
            tooltip.style.borderRadius = '4px';
            tooltip.style.fontSize = '12px';
            tooltip.style.pointerEvents = 'none';
            tooltip.style.whiteSpace = 'nowrap';
            tooltip.style.zIndex = '10000';
            tooltip.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
            tooltip.style.lineHeight = '1.4';
            tooltip.style.display = 'block';
            tooltip.style.visibility = 'visible';
            tooltip.style.opacity = '1';
            
            document.body.appendChild(tooltip);
            
            // Get mouse position
            const getMousePos = (e) => {
              if (e && typeof e === 'object') {
                if (e.clientX !== undefined && e.clientY !== undefined) {
                  return { x: e.clientX, y: e.clientY };
                }
                if (e.touches && e.touches[0]) {
                  return { x: e.touches[0].clientX, y: e.touches[0].clientY };
                }
              }
              return { x: window.event?.clientX || 0, y: window.event?.clientY || 0 };
            };
            
            // Set initial position
            const initialPos = getMousePos(event);
            tooltip.style.left = (initialPos.x + 10) + 'px';
            tooltip.style.top = (initialPos.y - 10) + 'px';
            
            // Use window mousemove for better tracking
            const mousemoveHandler = (e) => {
              const pos = getMousePos(e);
              if (pos.x > 0 || pos.y > 0) {
                tooltip.style.left = (pos.x + 10) + 'px';
                tooltip.style.top = (pos.y - 10) + 'px';
              }
            };
            window.addEventListener('mousemove', mousemoveHandler);
            
            labelGroup._tooltip = tooltip;
            labelGroup._mousemoveHandler = mousemoveHandler;
          };
          
          const hideLabelTooltip = () => {
            if (labelGroup._tooltip) {
              try {
                if (labelGroup._tooltip.parentNode) {
                  labelGroup._tooltip.parentNode.removeChild(labelGroup._tooltip);
                }
              } catch (e) {
                // Tooltip already removed or doesn't exist
              }
              if (labelGroup._mousemoveHandler) {
                window.removeEventListener('mousemove', labelGroup._mousemoveHandler);
              }
              delete labelGroup._tooltip;
              delete labelGroup._mousemoveHandler;
            }
          };
          
          text.addEventListener('mouseenter', showLabelTooltip);
          text.addEventListener('mouseleave', hideLabelTooltip);
          
          labelGroup.appendChild(text);
          labelsG.appendChild(labelGroup);
        } else {
          labelsG.appendChild(text);
        }
      });
      
      labelsSvg.appendChild(labelsG);
      labelsSvg.appendChild(relevanceG);
      this.$refs.heatmapLabelsContainer.appendChild(labelsSvg);
      
      // Create scrollable heatmap SVG (cells and x-axis labels)
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', scrollableWidth);
      svg.setAttribute('height', height);
      svg.setAttribute('class', 'heatmap-svg');
      
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('transform', `translate(${margin.left},${margin.top})`);
      
      // Draw special rows with circles (Combined score, GWAS support, Gene set support)
      const specialRowData = [
        { scores: combinedScores || [], label: 'Combined score' },
        { scores: gwasScores || [], label: 'GWAS support' },
        { scores: geneSetScores || [], label: 'Gene set support' }
      ];
      
      // Calculate min/max scores for circle size normalization
      const allScores = [];
      specialRowData.forEach(rowData => {
        rowData.scores.forEach(score => {
          if (score !== null && score !== undefined) {
            allScores.push(score);
          }
        });
      });
      const minScore = allScores.length > 0 ? Math.min(...allScores) : 0;
      const maxScore = allScores.length > 0 ? Math.max(...allScores) : 1;
      const minCircleRadius = Math.min(cellWidth, cellHeight) * 0.2; // 20% of cell size
      const maxCircleRadius = Math.min(cellWidth, cellHeight) * 0.4; // 40% of cell size
      
      specialRowData.forEach((rowData, rowIndex) => {
        genes.forEach((gene, geneIndex) => {
          const score = rowData.scores[geneIndex];
          const x = geneIndex * cellWidth; // No offset needed, relevance column is in y-axis labels
          const y = rowIndex * cellHeight;
          const centerX = x + cellWidth / 2;
          const centerY = y + cellHeight / 2;
          
          // Create cell group
          const cellGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          cellGroup.setAttribute('class', 'heatmap-cell-group');
          
          // Background rectangle (light gray for special rows) - append first so it's behind
          const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          rect.setAttribute('x', x);
          rect.setAttribute('y', y);
          rect.setAttribute('width', cellWidth);
          rect.setAttribute('height', cellHeight);
          rect.setAttribute('fill', '#f5f5f5');
          rect.setAttribute('stroke', '#fff');
          rect.setAttribute('stroke-width', '1');
          rect.setAttribute('class', 'heatmap-cell-bg');
          cellGroup.appendChild(rect);
          
          // Store score in closure for event handlers
          const cellScore = score;
          const cellRowLabel = rowData.label;
          
          // Create tooltip function
          const showTooltip = (event) => {
            if (cellScore !== null && cellScore !== undefined) {
              // Remove existing tooltip if any
              if (cellGroup._tooltip) {
                try {
                  if (cellGroup._tooltip.parentNode) {
                    cellGroup._tooltip.parentNode.removeChild(cellGroup._tooltip);
                  }
                } catch (e) {
                  // Tooltip already removed
                }
              }
              
              const tooltip = document.createElement('div');
              tooltip.className = 'heatmap-tooltip';
              tooltip.textContent = `${cellRowLabel}: ${cellScore.toFixed(2)}`;
              // Add inline styles to ensure visibility
              tooltip.style.position = 'fixed';
              tooltip.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
              tooltip.style.color = '#fff';
              tooltip.style.padding = '6px 10px';
              tooltip.style.borderRadius = '4px';
              tooltip.style.fontSize = '12px';
              tooltip.style.pointerEvents = 'none';
              tooltip.style.whiteSpace = 'nowrap';
              tooltip.style.zIndex = '10000';
              tooltip.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
              tooltip.style.lineHeight = '1.4';
              tooltip.style.display = 'block';
              tooltip.style.visibility = 'visible';
              tooltip.style.opacity = '1';
              
              document.body.appendChild(tooltip);
              
              // Get mouse position - SVG events should have clientX/Y
              const getMousePos = (e) => {
                // Try different event properties
                if (e && typeof e === 'object') {
                  if (e.clientX !== undefined && e.clientY !== undefined) {
                    return { x: e.clientX, y: e.clientY };
                  }
                  if (e.touches && e.touches[0]) {
                    return { x: e.touches[0].clientX, y: e.touches[0].clientY };
                  }
                }
                // Fallback: try to get from window event
                return { x: window.event?.clientX || 0, y: window.event?.clientY || 0 };
              };
              
              // Set initial position
              const initialPos = getMousePos(event);
              tooltip.style.left = (initialPos.x + 10) + 'px';
              tooltip.style.top = (initialPos.y - 10) + 'px';
              
              // Use window mousemove for better tracking
              const mousemoveHandler = (e) => {
                const pos = getMousePos(e);
                if (pos.x > 0 || pos.y > 0) {
                  tooltip.style.left = (pos.x + 10) + 'px';
                  tooltip.style.top = (pos.y - 10) + 'px';
                }
              };
              window.addEventListener('mousemove', mousemoveHandler);
              
              cellGroup._tooltip = tooltip;
              cellGroup._mousemoveHandler = mousemoveHandler;
            }
          };
          
          const hideTooltip = () => {
            if (cellGroup._tooltip) {
              document.body.removeChild(cellGroup._tooltip);
              if (cellGroup._mousemoveHandler) {
                window.removeEventListener('mousemove', cellGroup._mousemoveHandler);
              }
              delete cellGroup._tooltip;
              delete cellGroup._mousemoveHandler;
            }
          };
          
          // Add hover event listeners
          rect.addEventListener('mouseenter', showTooltip);
          rect.addEventListener('mouseleave', hideTooltip);
          
          // Circle with network color coding and variable size - append after rect so it's on top
          if (score !== null && score !== undefined) {
            const color = this.getColorByScore(score);
            const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            
            // Calculate circle radius based on score (normalized)
            let circleRadius;
            if (maxScore > minScore) {
              const normalizedScore = (score - minScore) / (maxScore - minScore);
              circleRadius = minCircleRadius + (maxCircleRadius - minCircleRadius) * normalizedScore;
            } else {
              circleRadius = (minCircleRadius + maxCircleRadius) / 2;
            }
            
            circle.setAttribute('cx', centerX);
            circle.setAttribute('cy', centerY);
            circle.setAttribute('r', circleRadius);
            circle.setAttribute('fill', color.background);
            circle.setAttribute('class', 'heatmap-circle-special');
            
            // Add hover events to circle as well
            circle.addEventListener('mouseenter', showTooltip);
            circle.addEventListener('mouseleave', hideTooltip);
            
            cellGroup.appendChild(circle);
          }
          
          g.appendChild(cellGroup);
        });
      });
      
      // Draw heatmap cells for factors (swapped: factors on y-axis, genes on x-axis)
      factors.forEach((factor, factorIndex) => {
        // Draw gene columns for this factor
        genes.forEach((gene, geneIndex) => {
          const value = data[geneIndex][factorIndex]; // Note: data is still [gene][factor]
          
          const x = geneIndex * cellWidth; // No offset needed, relevance column is in y-axis labels
          const y = (numSpecialRows + factorIndex) * cellHeight; // Offset by special rows
          
          // Create cell group
          const cellGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          cellGroup.setAttribute('class', 'heatmap-cell-group');
          
          // Background rectangle with purple gradient
          const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          rect.setAttribute('x', x);
          rect.setAttribute('y', y);
          rect.setAttribute('width', cellWidth);
          rect.setAttribute('height', cellHeight);
          rect.setAttribute('fill', getBackgroundColor(value));
          rect.setAttribute('stroke', '#fff');
          rect.setAttribute('stroke-width', '1');
          rect.setAttribute('class', 'heatmap-cell-bg');
          cellGroup.appendChild(rect);
          
          // Create tooltip function for factor cells
          const cellValue = value;
          const cellGene = gene;
          const cellFactorLabel = labels[factorIndex] || factor;
          
          const showCellTooltip = (event) => {
            if (cellValue !== null && cellValue !== undefined) {
              // Remove existing tooltip if any
              if (cellGroup._tooltip) {
                try {
                  if (cellGroup._tooltip.parentNode) {
                    cellGroup._tooltip.parentNode.removeChild(cellGroup._tooltip);
                  }
                } catch (e) {
                  // Tooltip already removed
                }
              }
              
              const tooltip = document.createElement('div');
              tooltip.className = 'heatmap-tooltip';
              tooltip.innerHTML = `${cellGene} - ${cellFactorLabel}<br>Relevance: ${cellValue.toFixed(2)}`;
              // Add inline styles to ensure visibility
              tooltip.style.position = 'fixed';
              tooltip.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
              tooltip.style.color = '#fff';
              tooltip.style.padding = '6px 10px';
              tooltip.style.borderRadius = '4px';
              tooltip.style.fontSize = '12px';
              tooltip.style.pointerEvents = 'none';
              tooltip.style.whiteSpace = 'nowrap';
              tooltip.style.zIndex = '10000';
              tooltip.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
              tooltip.style.lineHeight = '1.4';
              tooltip.style.display = 'block';
              tooltip.style.visibility = 'visible';
              tooltip.style.opacity = '1';
              
              document.body.appendChild(tooltip);
              
              // Get mouse position
              const getMousePos = (e) => {
                if (e && typeof e === 'object') {
                  if (e.clientX !== undefined && e.clientY !== undefined) {
                    return { x: e.clientX, y: e.clientY };
                  }
                  if (e.touches && e.touches[0]) {
                    return { x: e.touches[0].clientX, y: e.touches[0].clientY };
                  }
                }
                return { x: window.event?.clientX || 0, y: window.event?.clientY || 0 };
              };
              
              // Set initial position
              const initialPos = getMousePos(event);
              tooltip.style.left = (initialPos.x + 10) + 'px';
              tooltip.style.top = (initialPos.y - 10) + 'px';
              
              // Use window mousemove for better tracking
              const mousemoveHandler = (e) => {
                const pos = getMousePos(e);
                if (pos.x > 0 || pos.y > 0) {
                  tooltip.style.left = (pos.x + 10) + 'px';
                  tooltip.style.top = (pos.y - 10) + 'px';
                }
              };
              window.addEventListener('mousemove', mousemoveHandler);
              
              cellGroup._tooltip = tooltip;
              cellGroup._mousemoveHandler = mousemoveHandler;
            }
          };
          
          const hideCellTooltip = () => {
            if (cellGroup._tooltip) {
              document.body.removeChild(cellGroup._tooltip);
              if (cellGroup._mousemoveHandler) {
                window.removeEventListener('mousemove', cellGroup._mousemoveHandler);
              }
              delete cellGroup._tooltip;
              delete cellGroup._mousemoveHandler;
            }
          };
          
          rect.addEventListener('mouseenter', showCellTooltip);
          rect.addEventListener('mouseleave', hideCellTooltip);
          
          g.appendChild(cellGroup);
        });
      });
      
      // Draw gene labels (x-axis) - positioned higher to avoid overlap
      genes.forEach((gene, geneIndex) => {
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        const labelY = -30; // Move labels further up to avoid overlap with first row
        const labelX = geneIndex * cellWidth + cellWidth / 2; // No offset needed
        text.setAttribute('x', labelX);
        text.setAttribute('y', labelY);
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('class', 'heatmap-label');
        text.setAttribute('font-size', '11px');
        text.setAttribute('font-family', 'Arial');
        text.setAttribute('transform', `rotate(-45, ${labelX}, ${labelY})`);
        text.textContent = gene;
        g.appendChild(text);
      });
      
      svg.appendChild(g);
      this.$refs.heatmapContainer.appendChild(svg);
    },
    renderGeneSetsHeatmap() {
      console.log('renderGeneSetsHeatmap called', this.geneSetsHeatmapData);
      
      if (!this.geneSetsHeatmapData || !this.geneSetsHeatmapData.geneSets || this.geneSetsHeatmapData.geneSets.length === 0) {
        console.log('No gene sets heatmap data or empty gene sets array');
        return;
      }
      
      // Wait for refs to be available
      if (!this.$refs.geneSetsHeatmapContainer || !this.$refs.geneSetsHeatmapLabelsContainer) {
        console.log('geneSetsHeatmapContainer or geneSetsHeatmapLabelsContainer ref not found, retrying...');
        // Retry after a short delay
        setTimeout(() => {
          this.renderGeneSetsHeatmap();
        }, 100);
        return;
      }
      
      // Clear previous heatmap and clean up any orphaned tooltips
      // Remove any tooltips that might still be in the DOM
      const existingTooltips = document.querySelectorAll('.heatmap-tooltip');
      existingTooltips.forEach(tooltip => {
        try {
          if (tooltip.parentNode) {
            tooltip.parentNode.removeChild(tooltip);
          }
        } catch (e) {
          // Tooltip already removed
        }
      });
      
      // Clear previous heatmap
      this.$refs.geneSetsHeatmapContainer.innerHTML = '';
      this.$refs.geneSetsHeatmapLabelsContainer.innerHTML = '';
      
      let { geneSets, factors, factorLabels, factorScores, data } = this.geneSetsHeatmapData;
      const labels = factorLabels || factors;
      const relevanceScores = factorScores || factors.map(() => 0);
      
      console.log('Gene sets heatmap data:', { geneSets: geneSets.length, factors: factors.length, data: data ? data.length : 0 });
      
      if (geneSets.length === 0 || factors.length === 0) {
        console.log('Empty gene sets or factors array');
        return;
      }
      
      if (!data || data.length === 0) {
        console.log('No data array');
        return;
      }
      
      // Calculate dimensions: factors on y-axis, gene sets on x-axis
      const relevanceColumnWidth = 20;
      const cellWidth = Math.max(30, 300 / geneSets.length);
      const cellHeight = 20;
      const labelWidth = 200 + relevanceColumnWidth;
      const margin = { top: 120, right: 20, bottom: 20, left: 0 }; // Increased top margin for x-axis labels
      const labelMargin = { top: 120, right: 0, bottom: 20, left: 10 }; // Match top margin
      
      const scrollableWidth = Math.max(600, geneSets.length * cellWidth + margin.left + margin.right);
      const totalRows = factors.length;
      const height = totalRows * cellHeight + margin.top + margin.bottom;
      
      // Background color for cells - using purple gradient (same as main heatmap)
      const getBackgroundColor = (value) => {
        if (value === null || value === undefined || isNaN(value)) {
          return '#f0f0f0';
        }
        const clampedValue = Math.max(0, Math.min(1, value));
        const r1 = 243, g1 = 229, b1 = 245;
        const r2 = 156, g2 = 39, b2 = 176;
        const r = Math.round(r1 + (r2 - r1) * clampedValue);
        const g = Math.round(g1 + (g2 - g1) * clampedValue);
        const b = Math.round(b1 + (b2 - b1) * clampedValue);
        return `rgb(${r}, ${g}, ${b})`;
      };
      
      // Create fixed labels SVG (y-axis labels + relevance column)
      const labelsSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      labelsSvg.setAttribute('width', labelWidth);
      labelsSvg.setAttribute('height', height);
      labelsSvg.setAttribute('class', 'heatmap-labels-svg');
      
      // Group for factor labels
      const labelsG = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      labelsG.setAttribute('transform', `translate(${labelWidth - relevanceColumnWidth - 10},${labelMargin.top})`);
      
      // Group for relevance column
      const relevanceG = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      relevanceG.setAttribute('transform', `translate(${labelWidth - relevanceColumnWidth},${labelMargin.top})`);
      
      // Draw relevance column header label (rotated 90 degrees)
      const relevanceHeaderText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      const relevanceHeaderX = 10;
      const relevanceHeaderY = -60;
      relevanceHeaderText.setAttribute('x', relevanceHeaderX);
      relevanceHeaderText.setAttribute('y', relevanceHeaderY);
      relevanceHeaderText.setAttribute('text-anchor', 'middle');
      relevanceHeaderText.setAttribute('alignment-baseline', 'middle');
      relevanceHeaderText.setAttribute('class', 'heatmap-label');
      relevanceHeaderText.setAttribute('font-size', '10px');
      relevanceHeaderText.setAttribute('font-weight', '600');
      relevanceHeaderText.setAttribute('font-family', 'Arial');
      relevanceHeaderText.setAttribute('transform', `rotate(-90, ${relevanceHeaderX}, ${relevanceHeaderY})`);
      relevanceHeaderText.textContent = 'Relevance to trait';
      relevanceG.appendChild(relevanceHeaderText);
      
      // Draw relevance column cells for factor rows
      factors.forEach((factor, factorIndex) => {
        const relevanceScore = relevanceScores[factorIndex];
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', 0);
        rect.setAttribute('y', factorIndex * cellHeight);
        rect.setAttribute('width', relevanceColumnWidth);
        rect.setAttribute('height', cellHeight);
        
        if (relevanceScore !== null && relevanceScore !== undefined) {
          const color = this.getColorByScore(relevanceScore);
          rect.setAttribute('fill', color.background);
          rect.setAttribute('stroke', color.border);
          rect.setAttribute('stroke-width', '1');
          rect.setAttribute('title', `Relevance to trait: ${relevanceScore.toFixed(2)}`);
        } else {
          rect.setAttribute('fill', '#f0f0f0');
          rect.setAttribute('stroke', '#fff');
          rect.setAttribute('stroke-width', '1');
        }
        rect.setAttribute('class', 'heatmap-cell-bg');
        relevanceG.appendChild(rect);
      });
      
      // Draw factor labels (y-axis)
      factors.forEach((factor, factorIndex) => {
        const label = labels[factorIndex] || factor;
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', 0);
        text.setAttribute('y', factorIndex * cellHeight + cellHeight / 2);
        text.setAttribute('text-anchor', 'end');
        text.setAttribute('alignment-baseline', 'middle');
        text.setAttribute('class', 'heatmap-label');
        text.setAttribute('font-size', '11px');
        text.setAttribute('font-family', 'Arial');
        text.textContent = label;
        text.setAttribute('title', label); // Full label on hover
        labelsG.appendChild(text);
      });
      
      labelsSvg.appendChild(relevanceG);
      labelsSvg.appendChild(labelsG);
      this.$refs.geneSetsHeatmapLabelsContainer.appendChild(labelsSvg);
      
      // Create scrollable heatmap SVG
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', scrollableWidth);
      svg.setAttribute('height', height);
      svg.setAttribute('class', 'heatmap-svg');
      
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('transform', `translate(${margin.left},${margin.top})`);
      
      // Draw heatmap cells: factors (rows) x gene sets (columns)
      factors.forEach((factor, factorIndex) => {
        const rowData = data[factorIndex] || [];
        
        geneSets.forEach((geneSet, geneSetIndex) => {
          const value = rowData[geneSetIndex];
          
          const cellGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          cellGroup.setAttribute('class', 'heatmap-cell-group');
          
          // Background rectangle
          const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          rect.setAttribute('x', geneSetIndex * cellWidth);
          rect.setAttribute('y', factorIndex * cellHeight);
          rect.setAttribute('width', cellWidth);
          rect.setAttribute('height', cellHeight);
          rect.setAttribute('fill', getBackgroundColor(value));
          rect.setAttribute('stroke', '#fff');
          rect.setAttribute('stroke-width', '1');
          rect.setAttribute('class', 'heatmap-cell-bg');
          
          // Tooltip on hover
          const showCellTooltip = (event) => {
            if (!cellGroup._tooltip) {
              const tooltip = document.createElement('div');
              tooltip.className = 'heatmap-tooltip';
              tooltip.textContent = value !== null && value !== undefined 
                ? `Factor: ${labels[factorIndex]}\nGene Set: ${geneSet}\nRelevance: ${value.toFixed(4)}`
                : `Factor: ${labels[factorIndex]}\nGene Set: ${geneSet}\nRelevance: N/A`;
              
              tooltip.style.position = 'fixed';
              tooltip.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
              tooltip.style.color = '#fff';
              tooltip.style.padding = '6px 10px';
              tooltip.style.borderRadius = '4px';
              tooltip.style.fontSize = '12px';
              tooltip.style.pointerEvents = 'none';
              tooltip.style.whiteSpace = 'pre-line';
              tooltip.style.zIndex = '10000';
              tooltip.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
              tooltip.style.display = 'block';
              tooltip.style.visibility = 'visible';
              tooltip.style.opacity = '1';
              
              document.body.appendChild(tooltip);
              
              const getMousePos = (e) => {
                if (e && typeof e === 'object') {
                  if (e.clientX !== undefined && e.clientY !== undefined) {
                    return { x: e.clientX, y: e.clientY };
                  }
                  if (e.touches && e.touches[0]) {
                    return { x: e.touches[0].clientX, y: e.touches[0].clientY };
                  }
                }
                return { x: window.event?.clientX || 0, y: window.event?.clientY || 0 };
              };
              
              const initialPos = getMousePos(event);
              tooltip.style.left = (initialPos.x + 10) + 'px';
              tooltip.style.top = (initialPos.y - 10) + 'px';
              
              const mousemoveHandler = (e) => {
                const pos = getMousePos(e);
                if (pos.x > 0 || pos.y > 0) {
                  tooltip.style.left = (pos.x + 10) + 'px';
                  tooltip.style.top = (pos.y - 10) + 'px';
                }
              };
              window.addEventListener('mousemove', mousemoveHandler);
              
              cellGroup._tooltip = tooltip;
              cellGroup._mousemoveHandler = mousemoveHandler;
            }
          };
          
          const hideCellTooltip = () => {
            if (cellGroup._tooltip) {
              document.body.removeChild(cellGroup._tooltip);
              if (cellGroup._mousemoveHandler) {
                window.removeEventListener('mousemove', cellGroup._mousemoveHandler);
              }
              delete cellGroup._tooltip;
              delete cellGroup._mousemoveHandler;
            }
          };
          
          rect.addEventListener('mouseenter', showCellTooltip);
          rect.addEventListener('mouseleave', hideCellTooltip);
          
          cellGroup.appendChild(rect);
          g.appendChild(cellGroup);
        });
      });
      
      // Draw gene set labels (x-axis) - positioned to avoid overlap, left-aligned
      geneSets.forEach((geneSet, geneSetIndex) => {
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        const labelY = -15; // Position labels closer to heatmap cells
        const labelX = geneSetIndex * cellWidth; // Start at left edge of cell
        text.setAttribute('x', labelX);
        text.setAttribute('y', labelY);
        text.setAttribute('text-anchor', 'start'); // Left-align so text extends right and up when rotated
        text.setAttribute('alignment-baseline', 'middle');
        text.setAttribute('class', 'heatmap-label');
        text.setAttribute('font-family', 'Arial');
        // Rotate -45 degrees around the start point
        // The text will extend upward and to the right when rotated, avoiding cell overlap
        text.setAttribute('transform', `rotate(-45, ${labelX}, ${labelY})`);
        text.textContent = geneSet;
        g.appendChild(text);
      });
      
      svg.appendChild(g);
      this.$refs.geneSetsHeatmapContainer.appendChild(svg);
    },
    downloadHeatmap() {
      try {
        // Get both SVG elements
        const labelsContainer = this.$refs.heatmapLabelsContainer;
        const heatmapContainer = this.$refs.heatmapContainer;
        
        if (!labelsContainer || !heatmapContainer) {
          console.error('Heatmap containers not found');
          return;
        }
        
        const labelsSvg = labelsContainer.querySelector('svg');
        const heatmapSvg = heatmapContainer.querySelector('svg');
        
        if (!labelsSvg || !heatmapSvg) {
          console.error('SVG elements not found');
          return;
        }
        
        // Get dimensions
        const labelsWidth = parseFloat(labelsSvg.getAttribute('width')) || 0;
        const labelsHeight = parseFloat(labelsSvg.getAttribute('height')) || 0;
        const heatmapWidth = parseFloat(heatmapSvg.getAttribute('width')) || 0;
        const heatmapHeight = parseFloat(heatmapSvg.getAttribute('height')) || 0;
        
        // Create a combined SVG
        const combinedSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        combinedSvg.setAttribute('width', labelsWidth + heatmapWidth);
        combinedSvg.setAttribute('height', Math.max(labelsHeight, heatmapHeight));
        combinedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        combinedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        
        // Add style to ensure Arial font is used
        const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        style.textContent = 'text { font-family: Arial, sans-serif; }';
        combinedSvg.appendChild(style);
        
        // Create a group for labels (positioned at x=0)
        const labelsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        labelsGroup.setAttribute('transform', 'translate(0, 0)');
        // Clone all children from labels SVG and ensure font-family is set
        Array.from(labelsSvg.children).forEach(child => {
          const cloned = child.cloneNode(true);
          // Ensure all text elements have Arial font
          if (cloned.tagName === 'text' && !cloned.getAttribute('font-family')) {
            cloned.setAttribute('font-family', 'Arial');
          }
          // Recursively set font-family for nested text elements
          const textElements = cloned.querySelectorAll('text');
          textElements.forEach(textEl => {
            if (!textEl.getAttribute('font-family')) {
              textEl.setAttribute('font-family', 'Arial');
            }
          });
          labelsGroup.appendChild(cloned);
        });
        combinedSvg.appendChild(labelsGroup);
        
        // Create a group for heatmap (positioned after labels)
        const heatmapGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        const heatmapG = heatmapSvg.querySelector('g');
        if (heatmapG) {
          // Get current transform
          const currentTransform = heatmapG.getAttribute('transform') || '';
          // Extract translate values if any
          const translateMatch = currentTransform.match(/translate\(([^)]+)\)/);
          let translateX = 0, translateY = 0;
          if (translateMatch) {
            const coords = translateMatch[1].split(',');
            translateX = parseFloat(coords[0]) || 0;
            translateY = parseFloat(coords[1]) || 0;
          }
          // Set transform to position after labels
          heatmapGroup.setAttribute('transform', `translate(${translateX + labelsWidth}, ${translateY})`);
        } else {
          heatmapGroup.setAttribute('transform', `translate(${labelsWidth}, 0)`);
        }
        // Clone all children from heatmap SVG's main group and ensure font-family is set
        const cloneWithFontFamily = (element) => {
          const cloned = element.cloneNode(true);
          // Ensure all text elements have Arial font
          if (cloned.tagName === 'text' && !cloned.getAttribute('font-family')) {
            cloned.setAttribute('font-family', 'Arial');
          }
          // Recursively set font-family for nested text elements
          const textElements = cloned.querySelectorAll('text');
          textElements.forEach(textEl => {
            if (!textEl.getAttribute('font-family')) {
              textEl.setAttribute('font-family', 'Arial');
            }
          });
          return cloned;
        };
        
        if (heatmapG) {
          Array.from(heatmapG.children).forEach(child => {
            heatmapGroup.appendChild(cloneWithFontFamily(child));
          });
        } else {
          Array.from(heatmapSvg.children).forEach(child => {
            heatmapGroup.appendChild(cloneWithFontFamily(child));
          });
        }
        combinedSvg.appendChild(heatmapGroup);
        
        // Ensure SVG has proper viewBox for rendering
        if (!combinedSvg.getAttribute('viewBox')) {
          combinedSvg.setAttribute('viewBox', `0 0 ${labelsWidth + heatmapWidth} ${Math.max(labelsHeight, heatmapHeight)}`);
        }
        
        // Create ZIP file with SVG and data
        this.downloadAsZip(combinedSvg);
      } catch (error) {
        console.error('Error downloading heatmap:', error);
      }
    },
    downloadGeneSetsHeatmap() {
      try {
        // Get both SVG elements for gene sets heatmap
        const labelsContainer = this.$refs.geneSetsHeatmapLabelsContainer;
        const heatmapContainer = this.$refs.geneSetsHeatmapContainer;
        
        if (!labelsContainer || !heatmapContainer) {
          console.error('Gene sets heatmap containers not found');
          return;
        }
        
        const labelsSvg = labelsContainer.querySelector('svg');
        const heatmapSvg = heatmapContainer.querySelector('svg');
        
        if (!labelsSvg || !heatmapSvg) {
          console.error('Gene sets SVG elements not found');
          return;
        }
        
        // Get dimensions
        const labelsWidth = parseFloat(labelsSvg.getAttribute('width')) || 0;
        const labelsHeight = parseFloat(labelsSvg.getAttribute('height')) || 0;
        const heatmapWidth = parseFloat(heatmapSvg.getAttribute('width')) || 0;
        const heatmapHeight = parseFloat(heatmapSvg.getAttribute('height')) || 0;
        
        // Create a combined SVG
        const combinedSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        combinedSvg.setAttribute('width', labelsWidth + heatmapWidth);
        combinedSvg.setAttribute('height', Math.max(labelsHeight, heatmapHeight));
        combinedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        combinedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        combinedSvg.setAttribute('viewBox', `0 0 ${labelsWidth + heatmapWidth} ${Math.max(labelsHeight, heatmapHeight)}`);
        
        // Add style to ensure Arial font is used
        const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        style.textContent = 'text { font-family: Arial, sans-serif; }';
        combinedSvg.appendChild(style);
        
        // Create a group for labels (positioned at x=0)
        const labelsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        labelsGroup.setAttribute('transform', 'translate(0, 0)');
        // Clone all children from labels SVG and ensure font-family is set
        Array.from(labelsSvg.children).forEach(child => {
          const cloned = child.cloneNode(true);
          // Ensure all text elements have Arial font
          if (cloned.tagName === 'text' && !cloned.getAttribute('font-family')) {
            cloned.setAttribute('font-family', 'Arial');
          }
          // Recursively set font-family for nested text elements
          const textElements = cloned.querySelectorAll('text');
          textElements.forEach(textEl => {
            if (!textEl.getAttribute('font-family')) {
              textEl.setAttribute('font-family', 'Arial');
            }
          });
          labelsGroup.appendChild(cloned);
        });
        combinedSvg.appendChild(labelsGroup);
        
        // Create a group for heatmap (positioned after labels)
        const heatmapGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        const heatmapG = heatmapSvg.querySelector('g');
        if (heatmapG) {
          // Get current transform
          const currentTransform = heatmapG.getAttribute('transform') || '';
          // Adjust transform to account for labels width
          const adjustedTransform = currentTransform.replace(/translate\(([^,]+),([^)]+)\)/, 
            (match, x, y) => `translate(${parseFloat(x) + labelsWidth},${y})`);
          heatmapGroup.setAttribute('transform', adjustedTransform || `translate(${labelsWidth}, 0)`);
          
          // Clone all children from heatmap SVG and ensure font-family is set
          Array.from(heatmapG.children).forEach(child => {
            const cloned = child.cloneNode(true);
            // Ensure all text elements have Arial font
            if (cloned.tagName === 'text' && !cloned.getAttribute('font-family')) {
              cloned.setAttribute('font-family', 'Arial');
            }
            // Recursively set font-family for nested text elements
            const textElements = cloned.querySelectorAll('text');
            textElements.forEach(textEl => {
              if (!textEl.getAttribute('font-family')) {
                textEl.setAttribute('font-family', 'Arial');
              }
            });
            heatmapGroup.appendChild(cloned);
          });
        } else {
          // Fallback: clone entire heatmap SVG
          const cloned = heatmapSvg.cloneNode(true);
          cloned.setAttribute('transform', `translate(${labelsWidth}, 0)`);
          heatmapGroup.appendChild(cloned);
        }
        combinedSvg.appendChild(heatmapGroup);
        
        // Download as ZIP
        this.downloadGeneSetsHeatmapAsZip(combinedSvg);
      } catch (error) {
        console.error('Error downloading gene sets heatmap:', error);
      }
    },
    async downloadGeneSetsHeatmapAsZip(combinedSvg) {
      try {
        const zip = new JSZip();
        const timestamp = new Date().getTime();
        const phenotype = this.phenotypeName.replace(/[^a-zA-Z0-9]/g, '_');
        
        // Serialize SVG
        const serializer = new XMLSerializer();
        const svgData = serializer.serializeToString(combinedSvg);
        
        // Add SVG to ZIP
        zip.file(`gene_sets_heatmap_${phenotype}_${timestamp}.svg`, svgData);
        
        // Add data JSON
        const heatmapDataJson = {
          metadata: {
            phenotype: this.phenotypeName,
            timestamp: new Date().toISOString(),
            description: 'Gene set clusters × gene sets heatmap data'
          },
          geneSets: this.geneSetsHeatmapData.geneSets || [],
          factors: this.geneSetsHeatmapData.factors || [],
          factorLabels: this.geneSetsHeatmapData.factorLabels || [],
          factorScores: {
            data: this.geneSetsHeatmapData.factorScores || [],
            description: 'Array of gene set cluster relevance scores to phenotype'
          },
          data: {
            data: this.geneSetsHeatmapData.data || [],
            description: '2D array [gene set cluster][geneSet] of gene relevance to gene set cluster (factor_value)'
          }
        };
        
        zip.file(`gene_sets_heatmap_${phenotype}_${timestamp}_data.json`, JSON.stringify(heatmapDataJson, null, 2));
        
        // Generate and download ZIP
        const blob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `gene_sets_heatmap_${phenotype}_${timestamp}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error creating ZIP file:', error);
      }
    },
    createCanvasAndDownload(img, totalWidth, totalHeight, scale, combinedSvg) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = totalWidth * scale;
        canvas.height = totalHeight * scale;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          console.error('Failed to get canvas context');
          return;
        }
        
        // Set high quality rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Fill white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Use the actual image dimensions
        const imgWidth = img.naturalWidth || img.width || totalWidth;
        const imgHeight = img.naturalHeight || img.height || totalHeight;
        
        // Draw the image scaled up
        ctx.drawImage(img, 0, 0, imgWidth, imgHeight, 0, 0, totalWidth * scale, totalHeight * scale);
        
        // Verify canvas has content by checking a pixel
        const imageData = ctx.getImageData(0, 0, Math.min(10, canvas.width), Math.min(10, canvas.height));
        const hasContent = imageData.data.some((val, idx) => idx % 4 !== 3 && val !== 255); // Check if not all white (alpha channel is index 3)
        
        if (!hasContent) {
          console.warn('Canvas appears to be empty, downloading as SVG instead');
          // Try downloading as SVG instead
          this.downloadAsSVG(combinedSvg);
          return;
        }
        
        // Download as PNG - try toBlob first, fallback to toDataURL
        if (canvas.toBlob) {
          canvas.toBlob((blob) => {
            if (!blob || blob.size === 0) {
              console.warn('Blob creation failed or empty, trying data URL fallback');
              this.downloadFromDataURL(canvas);
              return;
            }
            try {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `heatmap_${this.phenotypeName || 'pigean'}_${new Date().getTime()}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              // Clean up URL after a short delay to ensure download starts
              setTimeout(() => {
                URL.revokeObjectURL(url);
              }, 100);
            } catch (error) {
              console.error('Error creating download link:', error);
              // Fallback to data URL
              this.downloadFromDataURL(canvas);
            }
          }, 'image/png');
        } else {
          // Browser doesn't support toBlob, use data URL
          this.downloadFromDataURL(canvas);
        }
      } catch (error) {
        console.error('Error creating canvas:', error);
        // Fallback: download as SVG
        const svgData = new XMLSerializer().serializeToString(img);
        this.downloadAsSVGString(svgData);
      }
    },
    downloadFromDataURL(canvas) {
      try {
        // Use toDataURL as fallback
        const dataUrl = canvas.toDataURL('image/png');
        if (!dataUrl || dataUrl === 'data:,') {
          console.error('Canvas toDataURL returned empty');
          return;
        }
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `heatmap_${this.phenotypeName || 'pigean'}_${new Date().getTime()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading from data URL:', error);
      }
    },
    downloadAsSVG(svgElement) {
      try {
        const svgData = new XMLSerializer().serializeToString(svgElement);
        this.downloadAsSVGString(svgData);
      } catch (error) {
        console.error('Error serializing SVG:', error);
      }
    },
    downloadAsSVGString(svgData) {
      try {
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `heatmap_${this.phenotypeName || 'pigean'}_${new Date().getTime()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);
      } catch (error) {
        console.error('Error downloading SVG:', error);
      }
    },
    async downloadAsZip(combinedSvg) {
      try {
        const zip = new JSZip();
        const timestamp = new Date().getTime();
        const baseName = `heatmap_${this.phenotypeName || 'pigean'}_${timestamp}`;
        
        // Add SVG file to ZIP
        const svgData = new XMLSerializer().serializeToString(combinedSvg);
        zip.file(`${baseName}.svg`, svgData);
        
        // Prepare data for JSON file
        if (this.heatmapData && this.heatmapData.genes && this.heatmapData.genes.length > 0) {
          const dataObject = {
            metadata: {
              phenotype: this.phenotypeName || '',
              timestamp: new Date().toISOString(),
              description: 'Heatmap data for PIGEAN factors visualization'
            },
            genes: this.heatmapData.genes || [],
            factors: this.heatmapData.factors || [],
            factorLabels: this.heatmapData.factorLabels || [],
            factorScores: this.heatmapData.factorScores || [],
            data: {
              description: '2D array [gene][factor] of gene relevance to factor',
              values: this.heatmapData.data || []
            },
            gwasData: {
              description: '2D array [gene][factor] of GWAS support scores (log_bf)',
              values: this.heatmapData.gwasData || []
            },
            geneSetData: {
              description: '2D array [gene][factor] of gene set support scores (prior)',
              values: this.heatmapData.geneSetData || []
            },
            combinedScores: {
              description: 'Array of combined scores per gene',
              values: this.heatmapData.combinedScores || []
            },
            gwasScores: {
              description: 'Array of GWAS scores per gene',
              values: this.heatmapData.gwasScores || []
            },
            geneSetScores: {
              description: 'Array of gene set scores per gene',
              values: this.heatmapData.geneSetScores || []
            }
          };
          
          // Add JSON data file to ZIP
          const jsonData = JSON.stringify(dataObject, null, 2);
          zip.file(`${baseName}_data.json`, jsonData);
        }
        
        // Generate ZIP file and download
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${baseName}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);
      } catch (error) {
        console.error('Error creating ZIP file:', error);
        // Fallback to SVG only if ZIP creation fails
        this.downloadAsSVG(combinedSvg);
      }
    }
  }
});
</script>
<style>
.factors-viz-container {
  /*border: 1px solid #ddd;
  border-radius: 4px;*/
  overflow: hidden;
}

.factors-viz-container.factors-viz-container--factor-data,
.factors-viz-container.factors-viz-container--network {
  overflow: visible;
  height: auto !important;
}

.factors-viz-container.factors-viz-container--network {
  min-height: 0;
}

.fbr-heatmap-toolbar {
  gap: 1rem;
  align-items: center;
  flex-wrap: nowrap;
}

.fbr-heatmap-toolbar-label {
  white-space: nowrap;
  flex-shrink: 0;
}

.fbr-heatmap-control-group {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
}

.fbr-heatmap-group-by-subcontrols {
  margin-left: 0;
  padding-left: 0.75rem;
  border-left: 1px solid #dee2e6;
}

.fbr-data-viz-ledger {
  min-width: 0;
  max-width: 100%;
}

.fbr-data-viz-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.5rem 0.75rem;
  padding: 0.4rem 0.55rem 0;
  background: #e8e3da;
  border: 1px solid #d4cdc2;
  border-bottom: none;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.fbr-data-viz-tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.15rem;
  flex: 0 1 auto;
  min-width: 0;
}

.fbr-data-viz-toolbar-end {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.55rem 0.85rem;
  flex: 1 1 auto;
  min-width: 0;
  padding-bottom: 0.35rem;
}

.fbr-data-viz-view-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 1rem;
  margin: 0 0 0.45rem;
  padding: 0.15rem 0 0.25rem;
  font-size: 12px;
  color: #444;
}

.fbr-data-viz-tab {
  flex: 0 0 auto;
  margin: 0 0 -1px;
  padding: 0.5rem 0.85rem 0.6rem;
  border: 1px solid #d4cdc2;
  border-bottom: solid 1px #ffffff;
  border-radius: 10px 10px 0 0;
  background: #f0ebe3;
  color: #33363d;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  cursor: pointer;
  appearance: none;
}

.fbr-data-viz-tab:not(.is-active):hover {
  background: #f6f2eb;
}

.fbr-data-viz-tab.is-active {
  background: #fff;
  color: #e07b39;
  font-weight: 700;
  border-color: #d4cdc2;
  border-bottom: 1px solid #fff;
  z-index: 1;
}

.fbr-data-viz-tab:focus-visible {
  outline: 2px solid #e07b39;
  outline-offset: 2px;
}

.fbr-data-viz-panel {
  padding: 0.5rem 0.55rem 0.65rem;
  border: 1px solid #d4cdc2;
  border-top: none;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  background: #fff;
  min-width: 0;
  max-width: 100%;
}

.fbr-data-viz-heatmap-pane {
  min-width: 0;
  max-width: 100%;
}

.fbr-data-network-panel {
  margin-top: 0;
}

.fbr-network-viz-wrap {
  position: relative;
  min-height: 450px;
}

.fbr-network-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.78);
  pointer-events: none;
}

.heatmap-tabs {
  margin-top: 10px;
}

.heatmap-tabs ::v-deep .nav-tabs {
  border-bottom: 2px solid #dee2e6;
  margin-bottom: 20px;
}

.heatmap-tabs ::v-deep .nav-link {
  color: #495057;
  padding: 10px 20px;
  font-weight: 500;
}

.heatmap-tabs ::v-deep .nav-link.active {
  color: #007bff;
  border-color: #dee2e6 #dee2e6 #fff;
  font-weight: 600;
}

.heatmap-tabs ::v-deep .tab-content {
  padding: 0;
}

.loading {
  padding: 20px;
  text-align: center;
  color: #666;
}

.error-alert {
  padding: 10px;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  margin: 10px;
}

.viz-legend {
  padding: 10px 15px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #ddd;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  font-size: 12px;
  align-items: center;
  justify-content: space-between;
}

.viz-legend--plain {
  padding: 4px 0 10px;
  background-color: transparent;
  border-bottom: none;
}

.legend-content {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: center;
  flex: 1;
}

.legend-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fbr-heatmap-axis-layout {
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.4rem;
  margin-left: 0.25rem;
}

.fbr-heatmap-axis-layout-label {
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  font-size: 12px;
}

.fbr-heatmap-axis-layout-select {
  max-width: 280px;
  min-width: 200px;
  width: auto;
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.15rem 0.5rem;
  font-size: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.legend-label {
  font-weight: 600;
  color: #333;
  margin-right: 4px;
}

.legend-text {
  color: #666;
}

.legend-color-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 8px;
}

.legend-filter-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 10px 0 0;
  cursor: pointer;
  user-select: none;
}

.viz-legend .legend-filter-checkbox {
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  box-sizing: border-box !important;
  display: inline-block !important;
  width: 14px !important;
  min-width: 14px !important;
  max-width: 14px !important;
  height: 14px !important;
  min-height: 14px !important;
  max-height: 14px !important;
  margin: 0 4px 0 0 !important;
  padding: 0 !important;
  border-radius: 2px;
  border: 1px solid rgba(0, 0, 0, 0.25);
  cursor: pointer;
  flex: 0 0 14px;
  position: relative;
  vertical-align: middle;
  background-color: #fff;
}

.viz-legend .legend-filter-checkbox:checked::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.viz-legend .legend-filter-checkbox.very-strong:checked {
  background-color: #4a90e2;
  border-color: #2c5aa0;
}

.viz-legend .legend-filter-checkbox.strongly-suggestive:checked {
  background-color: #f5a623;
  border-color: #d68910;
}

.viz-legend .legend-filter-checkbox.nominally-significant:checked {
  background-color: #f8e71c;
  border-color: #d4c716;
}

.viz-legend .legend-filter-checkbox.nominally-significant:checked::after {
  /* Dark check on yellow for contrast */
  border-color: #333;
}

.viz-legend .legend-filter-checkbox.not-significant:checked {
  background-color: #cccccc;
  border-color: #999999;
}

.viz-legend .legend-filter-checkbox.very-strong:not(:checked) {
  border-color: #4a90e2;
}

.viz-legend .legend-filter-checkbox.strongly-suggestive:not(:checked) {
  border-color: #f5a623;
}

.viz-legend .legend-filter-checkbox.nominally-significant:not(:checked) {
  border-color: #f8e71c;
}

.viz-legend .legend-filter-checkbox.not-significant:not(:checked) {
  border-color: #cccccc;
}

.legend-color {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.legend-color-text {
  font-size: 11px;
  color: #666;
  white-space: nowrap;
}

.legend-gene-sample {
  display: inline-block;
  font-size: 11px;
  color: #333;
  margin-right: 2px;
  font-family: Arial, sans-serif;
}

.legend-gene-sample-search {
  font-weight: 700;
}

.legend-gene-sample-context {
  font-weight: 300;
}

.legend-color.factor-relevance {
  background-color: #9c27b0; /* Dark purple matching heatmap gradient */
}

.fbr-heatmap-filter-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
  margin: 0;
  color: #444;
  white-space: nowrap;
}
.fbr-heatmap-filter-toggle.is-locked {
  opacity: 0.55;
  cursor: not-allowed;
}
.fbr-heatmap-filter-toggle input {
  margin: 0;
  cursor: pointer;
}
.fbr-heatmap-filter-toggle.is-locked input {
  cursor: not-allowed;
}

.legend-color.very-strong {
  background-color: #4a90e2;
}

.legend-color.strongly-suggestive {
  background-color: #f5a623;
}

.legend-color.nominally-significant {
  background-color: #f8e71c;
}

.legend-color.not-significant {
  background-color: #cccccc;
}

.heatmap-container {
  padding: 20px;
  overflow: visible;
  min-width: 0;
  max-width: 100%;
}

.heatmap-scrollable-wrapper {
  display: flex;
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
}

/* Webkit browsers (Chrome, Safari, Edge) - make scrollbar visible */
.heatmap-scrollable-wrapper::-webkit-scrollbar {
  width: 12px; /* Vertical scrollbar width */
}

.heatmap-scrollable-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 6px;
}

.heatmap-scrollable-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 6px;
}

.heatmap-scrollable-wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.heatmap-labels-fixed {
  position: sticky;
  left: 0;
  z-index: 9;
  background-color: #fff;
  border-right: 2px solid #ddd;
  flex-shrink: 0;
}

.heatmap-content-scrollable {
  flex: 1;
  overflow-x: scroll; /* Changed from auto to scroll to always show scrollbar */
  overflow-y: hidden;
  scrollbar-width: thin; /* Firefox - make scrollbar visible */
  scrollbar-color: #888 #f1f1f1; /* Firefox - thumb and track colors */
  min-width: 0; /* Allow flex item to shrink below content size */
  width: 0; /* Force flex item to respect container width */
}

/* Webkit browsers (Chrome, Safari, Edge) - make scrollbar visible */
.heatmap-content-scrollable::-webkit-scrollbar {
  height: 12px; /* Horizontal scrollbar height */
  display: block; /* Ensure scrollbar is always displayed */
}

.heatmap-content-scrollable::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 6px;
  -webkit-box-shadow: inset 0 0 2px rgba(0,0,0,0.1);
}

.heatmap-content-scrollable::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 6px;
  -webkit-box-shadow: inset 0 0 2px rgba(0,0,0,0.2);
}

.heatmap-content-scrollable::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.heatmap-labels-svg {
  font-family: Arial, sans-serif;
  margin-right: 10px;
}

.heatmap-wrapper {
  display: inline-block;
  min-width: 100%;
}

.heatmap-svg {
  font-family: Arial, sans-serif;
}

.heatmap-cell {
  cursor: pointer;
}

.heatmap-cell:hover {
  stroke: #333 !important;
  stroke-width: 2px !important;
}

.heatmap-score-text {
  fill: #333;
  font-weight: 600;
  pointer-events: none;
  user-select: none;
}

.heatmap-cell-group {
  cursor: pointer;
}

.heatmap-tooltip {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 10000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  line-height: 1.4;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.heatmap-tooltip br {
  display: block;
  content: "";
  margin-top: 2px;
}
</style>
<style>
/* Heatmap labels (SVG text created in JS) - scoped to wrapper id so they apply to dynamic content */
#factor-base-reveal-heatmap-wrapper .heatmap-label {
  font-size: 12px !important;
  font-weight: 300 !important;
  fill: #333;
}

#factor-base-reveal-heatmap-wrapper .heatmap-label.heatmap-label-search-gene {
  font-weight: 700 !important;
}

#factor-base-reveal-heatmap-wrapper .heatmap-label.heatmap-label-context-gene {
  font-weight: 300 !important;
}

#factor-base-reveal-heatmap-wrapper .heatmap-label-score-row {
  font-weight: 700 !important;
}

#factor-base-reveal-heatmap-wrapper .heatmap-label.heatmap-label-selected {
  fill: #ff6600 !important;
  font-weight: 700 !important;
}

/* Global styles for tooltip since it's appended to body */
.heatmap-tooltip {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 10000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  line-height: 1.4;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.heatmap-tooltip br {
  display: block;
  content: "";
  margin-top: 2px;
}
</style>
